// SƠ ĐỒ ĐỒ THỊ NHIỀU MẠNG — LÕI THUẬT TOÁN THUẦN (GĐ-A/B1, 07/10/2026). Không IO, không mạng, không import khối/YAML (chỉ KIỂU của bản đồ + luật thứ tự đã có ở so-do-thu-tu.ts).
//
// Bối cảnh: MỘT hồ sơ trình duyệt = MỘT bot, đăng nhập sẵn nhiều mạng; một lúc bot chỉ làm MỘT việc. "Trong lúc nhánh này Nghỉ thì nhánh kia làm" = xen kẽ TUẦN TỰ do bộ điều phối ở Boss quyết,
// nên mô hình KHÔNG là đồ thị tuỳ ý mà là TẬP CÁC ĐƯỜNG RỜI NHAU (mỗi nút vào ≤ 1, ra ≤ 1, không vòng). Mỗi đường được cắt thành các ĐOẠN: một đoạn = các nút liền nhau CÙNG MỘT MẠNG = đúng một
// kịch bản sơ đồ cũ (dichSoDo dịch được, cùng YAML / cùng hash như trước); giữa các đoạn là ô Nghỉ (mục "nghi" — bộ điều phối nhường lượt cho đường khác trong lúc nghỉ).
//   · Cắt BẮT BUỘC khi đổi mạng giữa hai nút kề (một bundle chỉ có MỘT URL đích).
//   · Cắt TUỲ CHỌN tại ô Nghỉ khi cờ `nhuong` bật (mặc định bật khi đồ thị có ≥ 2 đường). Chỉ cắt khi đoạn sau KHÔNG đổi ngữ nghĩa — canCat/kiemCat (xem ghi chú ở đó); không an toàn → KHÔNG cắt
//     (đoạn dài hơn nhưng đúng) + cảnh báo {nutId, lyDo, cachSua}.
// Tương thích ngược: đồ thị KHÔNG có `canh` = MỘT chuỗi theo thứ tự nut[] → một đường → (nhường tắt mặc định) một đoạn có đủ nút → dichSoDo cho cùng YAML / hash như kịch bản cũ.
//
// Việc CHƯA làm ở đây (tích hợp sau): id nút ổn định ở so-do-sua.ts danhSo / so-do.ts chuanHoaSoDo (hiện đánh lại "n1…" theo vị trí); dịch từng đoạn thành bundle riêng; bộ điều phối.
import type { BanDo } from "./ban-do";
import type { KhoiMau } from "./tu-dung";
import { SO_NUT_TOI_DA, type CanhLap, type LapCaSoDo, type NutSoDo, type SoDo, type TanSuatNut } from "./so-do-sua";
import { nhanLan } from "./lan-dung";
import { cachTuTimKeHoach, lanTheoNut, loiCanDanhSach, loiNghiLien, loiToHop, mucThuTuHieuLuc, thuTuCua, thuTuTuTimCua, tuKhoaTuTim, type CachTuTim, type MucThuTu, type ThuTuThaoTac, type ThuTuTuTim } from "./so-do-thu-tu";

// ================= KIỂU =================
export type NutDoThi = NutSoDo & { nenTang?: string; nhuong?: boolean };
export interface CanhDoThi { tu: string; den: string }
export type { CanhLap, LapCaSoDo, TanSuatNut };
/** (đợt A) Tần suất "cứ N ngày": N trong khoảng này. Nghỉ ở chỗ CẮT (Boss giữ đồng hồ) tối đa 6 giờ; nghỉ nằm trong đoạn vẫn theo trần kỹ thuật 1 giờ (khuyen-nghi-so.ts). */
export const GIOI_HAN_TAN_SUAT = { nToiThieu: 2, nToiDa: 60 } as const;
export const NGHI_CAT_TOI_DA_S = 6 * 3600;
/**
 * `nenTang` = mạng MẶC ĐỊNH (nút không khai `nenTang` riêng thì thuộc mạng này). `canh` thiếu = chuỗi theo thứ tự `nut`.
 * (07/10/2026) VÒNG LẶP: `lap` = các cạnh "quay lại" (KHÔNG thuộc `canh` — đường vẫn có ô đầu, ô cuối); `lapCaSoDo` = chạy lại cả sơ đồ nhiều vòng. Cả hai là chuyện ĐIỀU PHỐI (luot-do-thi.ts),
 * không vào bundle: đổi số lần / giờ không đổi hash kịch bản con. Vòng quay về giữa một đoạn thì đoạn bị cắt đúng ở biên vòng (cắt phải an toàn như cắt tại ô Nghỉ — kiemCat — không thì lỗi cứng).
 */
export interface DoThi { nut: NutDoThi[]; canh?: CanhDoThi[]; lap?: CanhLap[]; lapCaSoDo?: LapCaSoDo; nenTang: string }

/** Trần (người dùng duyệt): mỗi đoạn ≤ 20 nút (= SO_NUT_TOI_DA), cả đồ thị ≤ 60 nút, ≤ 6 đường, ≤ 40 đoạn mỗi lượt. */
export const GIOI_HAN_DO_THI = { nut: 60, duong: 6, nutMoiDoan: SO_NUT_TOI_DA, doanMoiLuot: 40 } as const;
/**
 * Trần VÒNG LẶP (không bao giờ chạy mãi): số lần lặp THÊM 1..20 (mặc định 2), lặp cả sơ đồ 1..20 vòng, nghỉ giữa vòng ≤ 6 giờ, một đường sau khi bung mọi vòng ≤ 200 mục (đoạn + nghỉ),
 * vòng không qua ô Nghỉ mà lặp > 3 lần → cảnh báo vàng; Chạy thử (Nháp) rút còn tối đa 2 vòng (mỗi cạnh lặp và cả sơ đồ).
 */
export const GIOI_HAN_LAP = { soLanToiThieu: 1, soLanToiDa: 20, soLanMacDinh: 2, soVongToiDa: 20, nghiGiuaVongToiDaS: 6 * 3600, mucSauBung: 200, soLanKhongNghi: 3, vongNhap: 2 } as const;
/** Tổng số VÒNG của một cạnh lặp (lần đầu + soLan lần thêm); Nháp rút còn ≤ 2 vòng. */
export const soVongLap = (soLan: number, nhap: boolean): number => (nhap ? Math.min(GIOI_HAN_LAP.vongNhap, soLan + 1) : soLan + 1);
/** Tổng số vòng của "lặp cả sơ đồ"; Nháp rút còn ≤ 2. */
export const soVongCaSoDo = (soVong: number, nhap: boolean): number => (nhap ? Math.min(GIOI_HAN_LAP.vongNhap, soVong) : soVong);
const DO_DAI_ID_TOI_DA = 64;

export class LoiDoThi extends Error {
  constructor(public loi: string[]) { super(loi.join("; ")); }
}

// ================= BẢN ĐỒ DÙNG ĐỂ PHÂN TÍCH =================
/** Một thao tác ở dạng PHÂN TÍCH: bản đồ công khai (không YAML) + thông tin "khép kín" tính từ khối đầy đủ (banDoPhanTich). `ThaoTacCongKhai` cũng khớp kiểu này (thiếu 4 trường cuối → xem kiemCat). */
export interface ThaoTacPhanTich {
  ma: string;
  tenNgan: string;
  thuTu: ThuTuThaoTac;
  thuTuTuTim?: ThuTuTuTim;
  thamSo: readonly { ten: string; macDinh?: string }[];
  /** Biến / tham số khối ĐỌC nhưng không tự tạo, không tự khai, không phải danh sách của luật for_each (loiCanDanhSach) và không do runner cấp sẵn — tức khối KHÔNG khép kín. */
  phuThuocNgoai?: readonly string[];
  /** (08/10/2026) phần của phuThuocNgoai chỉ đọc MỀM (khoá `luot` nguon_nhom / nhom_da_vao / tu_khoa_nhom_da_vao — thiếu thì runner tự lùi về «Nhóm của bạn» / trang chủ): cắt khỏi khối cung cấp vẫn an toàn (vàng). */
  phuThuocMem?: readonly string[];
  /** Biến khối tự tạo (into) — "danh sách / giá trị HIỆN TẠI": khối đứng sau dùng bản do khối GẦN NHẤT phía trước tạo. */
  taoBien?: readonly string[];
  /** Tham số kịch bản khối khai (params) — tên tham số là CHUNG cả kịch bản: khối ĐỌC không khai tham số này dùng bản của khối khai ĐẦU TIÊN (lần dùng thứ n ≥ 2 của khối chỉ đổi tên tham số của CHÍNH nó). */
  khaiThamSo?: readonly string[];
  /** Khối có bước AI chấm "liên quan" (chamPhuHop) — bản chính; `coChamPhuHopTuTim` = của biến thể tự tìm (chỉ khối có biến thể). */
  coChamPhuHop?: boolean;
  coChamPhuHopTuTim?: boolean;
  /** Danh sách mà khối chạy VÒNG XEM / ĐỌC (for_each cấp đầu CÓ bước con) — gộp bản chính + biến thể tự tìm. sinhKichBan (xemRoiLam / L-71) chèn bước lọc / chấm vào TRƯỚC vòng này khi một khối SAU làm trên cùng danh sách. */
  vongXem?: readonly string[];
  /** Danh sách mà khối LÀM VIỆC trên đó (for_each cấp đầu hoặc chamPhuHop.danhSach). */
  lamTrenDs?: readonly string[];
  /** Như vongXem / lamTrenDs nhưng của BIẾN THỂ TỰ TÌM (khi để trống địa chỉ) — chỉ có ở khối có biến thể. */
  vongXemTuTim?: readonly string[];
  lamTrenDsTuTim?: readonly string[];
}
export interface BanDoPhanTich { nenTang: readonly { ma: string; thaoTac: readonly ThaoTacPhanTich[] }[] }

const BIEN_SAN = new Set(["params", "secrets", "knowledge", "ledger", "sources", "hoi", "item", "ts", "ts_url", "goc"]);
/** Biến do RUNNER cấp lúc chạy (không do khối khác tạo): {{cau_da_go}} = câu đã gõ thật (runner.ts ctxHauKiem). */
const BIEN_RUNNER = new Set(["cau_da_go"]);
const TU_KHOA_BT = new Set(["and", "or", "not", "in", "true", "false", "null", "None", "True", "False"]);

/** Tên biến (gốc) trong một biểu thức: bỏ chuỗi nháy, thuộc tính sau dấu chấm, số, từ khoá và tên hàm. */
function bienTrongBieuThuc(bt: string): string[] {
  const s = bt.replace(/"(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'/g, '""');
  const ra: string[] = [];
  for (const m of s.matchAll(/[A-Za-z_][A-Za-z0-9_]*/g)) {
    const i = m.index!, ten = m[0];
    const truoc = s.slice(0, i);
    if (/\.\s*$/.test(truoc) || /[0-9]$/.test(truoc)) continue;
    if (TU_KHOA_BT.has(ten) || /^\s*\(/.test(s.slice(i + ten.length))) continue;
    ra.push(ten);
  }
  return ra;
}
function mocChuoi(v: unknown, ra: string[]): void {
  if (typeof v === "string") ra.push(v);
  else if (Array.isArray(v)) for (const x of v) mocChuoi(x, ra);
  else if (v && typeof v === "object") for (const x of Object.values(v)) mocChuoi(x, ra);
}
function mocBieuThucHoc(v: unknown, ra: string[], khoa = ""): void {
  if (typeof v === "string") { if (khoa === "dk" || khoa === "when" || khoa === "filter") ra.push(v); }
  else if (Array.isArray(v)) for (const x of v) mocBieuThucHoc(x, ra, khoa);
  else if (v && typeof v === "object") for (const [k, x] of Object.entries(v)) mocBieuThucHoc(x, ra, k);
}
/** Giá trị của khoá `nguon_nhom` trong phần đã học của bước lướt: tên BIẾN danh sách nhóm đọc "mềm" (nhóm đã tham gia trong danh sách tìm nhóm) — không bọc {{ }}. */
function mocThamChieu(v: unknown, khoa: string, ra: string[]): void {
  if (Array.isArray(v)) for (const x of v) mocThamChieu(x, khoa, ra);
  else if (v && typeof v === "object") for (const [k, x] of Object.entries(v)) mocThamChieu(x, k, ra);
  else if (typeof v === "string" && khoa === "nguon_nhom") ra.push(v);
}

/**
 * Cấu trúc "khép kín" của một khối (đọc từ khối đầy đủ): biến tự do = biến khối đọc − biến khối tự tạo − tham số khối khai − biến runner cấp − danh sách luật for_each đã giữ (canDs).
 * Đọc từ: mọi {{…}} của yamlSteps + phần đã học, `for_each` / `when` / `filter` của yamlSteps, `dk` / `when` / `filter` của phần đã học, tên biến `luot.nguon_nhom`; tham chiếu id bước
 * (`ds_tu_khoa.tim`, `chi_khi_xong`) phải là id của chính khối (khác → "buoc:<id>").
 */
export function khepKinCuaKhoi(k: KhoiMau, canDs: readonly string[]): { phuThuocNgoai: string[]; phuThuocMem: string[]; taoBien: string[]; khaiThamSo: string[] } {
  const tao = new Set<string>();
  for (const m of k.yamlSteps.matchAll(/^\s+into:\s*([A-Za-z_][A-Za-z0-9_]*)\s*$/gm)) tao.add(m[1]!);
  const khai = new Set(Object.keys(k.params ?? {}));
  const dung = new Set<string>();
  // (08/10/2026) biến CHỈ đọc trong các khoá "mềm" của `luot` (nguon_nhom / nhom_da_vao / tu_khoa_nhom_da_vao — runner.ts runLuot: thiếu thì tự chọn nhóm từ "Nhóm của bạn",
  // không có nữa thì trang chủ) là phụ thuộc MỀM: tách khỏi khối cung cấp vẫn chạy đúng (đường lùi), chỉ đổi nguồn nhóm → kiemCat không chặn, tachDoan báo vàng.
  const KHOA_MEM = new Set(["nguon_nhom", "nhom_da_vao", "tu_khoa_nhom_da_vao"]);
  const chuMem: string[] = [];
  const hocCung = (v: unknown, khoa: string): unknown => {
    if (Array.isArray(v)) return v.map((x) => hocCung(x, khoa));
    if (v && typeof v === "object") return Object.fromEntries(Object.entries(v).flatMap(([kk, x]) => (KHOA_MEM.has(kk) ? (mocChuoi(x, chuMem), []) : [[kk, hocCung(x, kk)]])));
    return v;
  };
  const hocKhongMem = hocCung(k.learned, "");
  const dungMem = new Set<string>();
  for (const s of chuMem) for (const m of s.matchAll(/\{\{([\s\S]+?)\}\}/g)) for (const n of bienTrongBieuThuc(m[1]!)) dungMem.add(n);
  const chu: string[] = [k.yamlSteps];
  mocChuoi(hocKhongMem, chu);
  for (const s of chu) for (const m of s.matchAll(/\{\{([\s\S]+?)\}\}/g)) for (const n of bienTrongBieuThuc(m[1]!)) dung.add(n);
  for (const m of k.yamlSteps.matchAll(/^\s+for_each:\s*(.+)$/gm)) for (const n of bienTrongBieuThuc(m[1]!)) dung.add(n);
  for (const m of k.yamlSteps.matchAll(/^\s+(?:when|filter):\s*"?(.+?)"?\s*$/gm)) for (const n of bienTrongBieuThuc(m[1]!)) dung.add(n);
  const bt: string[] = [];
  mocBieuThucHoc(hocKhongMem, bt);
  for (const b of bt) for (const n of bienTrongBieuThuc(b)) dung.add(n);
  const nguon: string[] = [];
  mocThamChieu(k.learned, "", nguon);
  for (const n of nguon) dungMem.add(n);
  const tuDo = (n: string): boolean => !BIEN_SAN.has(n) && !BIEN_RUNNER.has(n) && !tao.has(n) && !khai.has(n) && !canDs.includes(n);
  const mem = [...dungMem].filter((n) => tuDo(n) && !dung.has(n));
  const ngoai = [...new Set([...dung, ...mem])].filter(tuDo);
  // tham chiếu id bước: phải là bước của chính khối
  const idCuaKhoi = new Set([...k.yamlSteps.matchAll(/^\s*- id:\s*([a-zA-Z0-9_]+)/gm)].map((m) => m[1]!));
  const tc: string[] = [];
  const duyetTc = (v: unknown, khoa: string): void => {
    if (Array.isArray(v)) { for (const x of v) duyetTc(x, khoa); return; }
    if (v && typeof v === "object") { for (const [kk, x] of Object.entries(v)) duyetTc(x, kk); return; }
    if (typeof v === "string" && (khoa === "tim" || khoa === "chi_khi_xong") && !idCuaKhoi.has(v) && !Object.keys(k.learned ?? {}).includes(v)) tc.push(`buoc:${v}`);
  };
  duyetTc(k.learned, "");
  return { phuThuocNgoai: [...ngoai, ...tc].sort(), phuThuocMem: mem.sort(), taoBien: [...tao].sort(), khaiThamSo: [...khai].sort() };
}

/** Danh sách khối chạy vòng xem (for_each cấp đầu + steps) và danh sách khối làm việc trên (for_each cấp đầu / chamPhuHop): bản CHÍNH, và (khối có tuTim) bản biến thể tự tìm — hợp của cách lắp "day_du" và "chi_lam". */
export function vongXemCuaKhoi(k: KhoiMau): { vongXem: string[]; lamTrenDs: string[]; vongXemTuTim?: string[]; lamTrenDsTuTim?: string[] } {
  const quet = (phan: string[], chamPhuHop: KhoiMau["chamPhuHop"]): { vong: string[]; lam: string[] } => {
    const vong = new Set<string>(), lam = new Set<string>();
    for (const y of phan) for (const c of y.split(/^(?=  - id:)/m)) {
      const ds = /^    for_each:\s*([A-Za-z_][A-Za-z0-9_]*)\s*$/m.exec(c)?.[1];
      if (!ds) continue;
      lam.add(ds);
      if (/^    steps:/m.test(c)) vong.add(ds);
    }
    if (chamPhuHop) lam.add(chamPhuHop.danhSach);
    return { vong: [...vong].sort(), lam: [...lam].sort() };
  };
  const chinh = quet([k.yamlSteps], k.chamPhuHop);
  const tt = k.tuTim ? quet([k.tuTim.yamlTim, k.tuTim.yamlSteps], k.tuTim.chamPhuHop) : undefined;
  return { vongXem: chinh.vong, lamTrenDs: chinh.lam, ...(tt ? { vongXemTuTim: tt.vong, lamTrenDsTuTim: tt.lam } : {}) };
}

/** Dựng BanDoPhanTich từ bản đồ ĐẦY ĐỦ (máy chủ / test): cấu trúc thứ tự công khai (thuTuCua, thuTuTuTimCua — như banDoCongKhai) + thông tin khép kín. Không mang yamlSteps / selector. */
export function banDoPhanTich(banDo: BanDo): BanDoPhanTich {
  return {
    nenTang: banDo.nenTang.map((n) => ({
      ma: n.ma,
      thaoTac: n.thaoTac.map((t): ThaoTacPhanTich => {
        const thuTu = thuTuCua({ khoi: t.khoi });
        const kk = khepKinCuaKhoi(t.khoi, thuTu.canDs);
        const vx = vongXemCuaKhoi(t.khoi);
        return {
          ...vx,
          ma: t.ma, tenNgan: t.tenNgan, thuTu,
          ...(t.khoi.tuTim ? { thuTuTuTim: thuTuTuTimCua(t.khoi) } : {}),
          thamSo: t.thamSo.map((d) => ({ ten: d.ten, ...(d.macDinh !== undefined ? { macDinh: d.macDinh } : {}) })),
          phuThuocNgoai: kk.phuThuocNgoai, phuThuocMem: kk.phuThuocMem, taoBien: kk.taoBien, khaiThamSo: kk.khaiThamSo,
          coChamPhuHop: !!t.khoi.chamPhuHop,
          ...(t.khoi.tuTim ? { coChamPhuHopTuTim: !!t.khoi.tuTim.chamPhuHop } : {}),
        };
      }),
    })),
  };
}

const NHO_PHAN_TICH = new WeakMap<BanDo, BanDoPhanTich>();
/** banDoPhanTich có nhớ theo ĐỐI TƯỢNG bản đồ (một yêu cầu gọi chuanHoaSoDo nhiều lần với cùng một bản đồ — dựng cấu trúc 56 khối chỉ một lần). */
export function banDoPhanTichNho(banDo: BanDo): BanDoPhanTich {
  const cu = NHO_PHAN_TICH.get(banDo);
  if (cu) return cu;
  const moi = banDoPhanTich(banDo);
  NHO_PHAN_TICH.set(banDo, moi);
  return moi;
}
/** Sơ đồ (đã chuẩn hoá) → đồ thị: cùng nút / cạnh, mạng mặc định = nenTang của sơ đồ. */
export const doThiTuSoDo = (sd: Pick<SoDo, "nut" | "nenTang" | "canh"> & Partial<Pick<SoDo, "lap" | "lapCaSoDo">>): DoThi => ({ nenTang: sd.nenTang, nut: sd.nut, ...(sd.canh ? { canh: sd.canh } : {}), ...(sd.lap?.length ? { lap: sd.lap } : {}), ...(sd.lapCaSoDo ? { lapCaSoDo: sd.lapCaSoDo } : {}) });
/** Tuỳ chọn tách đoạn của một sơ đồ: hoSoTen / mucTieu PHẢI đúng như lúc dịch từng đoạn (chuanHoaSoDo: tên sản phẩm, mục tiêu đã gọn một dòng). */
export const tuyChonTachCua = (sd: Pick<SoDo, "hoSo" | "mucTieu">, banDo: BanDo): TuyChonTach => ({ banDo: banDoPhanTichNho(banDo), hoSoTen: sd.hoSo?.ten ?? null, mucTieu: sd.mucTieu || null });

// ================= BỐI CẢNH / TUỲ CHỌN =================
/** Bối cảnh phân tích: bản đồ + sản phẩm (tên) + mục tiêu của kịch bản — quyết định từ khoá tự tìm và AI chấm liên quan, nên PHẢI giống lúc dịch từng đoạn. */
export interface BoiCanhDoThi { banDo: BanDoPhanTich; hoSoTen?: string | null; mucTieu?: string | null }
export interface TuyChonTach extends BoiCanhDoThi {
  /** Mặc định cho cờ `nhuong` của ô Nghỉ không khai riêng. Không đặt: bật khi đồ thị có ≥ 2 đường, tắt khi 1 đường. */
  nhuongMacDinh?: boolean;
  /** (duKienKeHoach) ước lượng số giây của một đoạn — người gọi có thể tính bằng uocTinhGiaySoDo trên đoạn đã dịch; không có thì mục đoạn không có `uocS`. */
  uocS?: (d: MucDoan) => number | undefined;
}

export interface CanhBaoDoThi { loai: string; nutId?: string; nutAnhHuong?: string; lyDo: string; cachSua?: string }
export type LoaiKhongCat = "can_danh_sach" | "dung_lai_danh_sach" | "tu_khoa_tu_tim" | "doi_cau_truc" | "buoc_chen_cheo" | "cham_lien_quan" | "khoi_khong_khep_kin" | "khong_phan_tich_duoc";
/** Lý do KHÔNG cắt tại một ô Nghỉ: `nutId` = ô Nghỉ người dùng muốn cắt; `nutAnhHuong` = nút bị đổi nghĩa. */
export interface CanhBaoCat extends CanhBaoDoThi { loai: LoaiKhongCat; nutId: string; lyDo: string; cachSua: string }

// ================= CẤU TRÚC: chuẩn hoá + tách ĐƯỜNG =================
const laChuoi = (v: unknown): v is string => typeof v === "string";
const laObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
const DIEU_KHIEN = /[\u0000-\u001f\u007f]/;
const idHopLe = (v: unknown): v is string => laChuoi(v) && v.length > 0 && Array.from(v).length <= DO_DAI_ID_TOI_DA && !DIEU_KHIEN.test(v);
const saoNut = (n: NutDoThi): NutDoThi => ({ id: n.id, thaoTac: n.thaoTac, thamSo: { ...n.thamSo }, ...(n.tuDuyet === true ? { tuDuyet: true } : {}), ...(n.nenTang !== undefined ? { nenTang: n.nenTang } : {}), ...(n.nhuong !== undefined ? { nhuong: n.nhuong } : {}), ...(n.danhCho !== undefined ? { danhCho: n.danhCho } : {}), ...(n.tanSuat !== undefined ? { tanSuat: { ...n.tanSuat } } : {}) });
/** (đợt A) Đọc tần suất của một ô: thiếu / null → undefined; sai → null (nơi gọi báo lỗi). */
function docTanSuat(v: unknown): TanSuatNut | undefined | null | "n" {
  if (v === undefined || v === null) return undefined;
  if (!laObject(v)) return null;
  if (v.kieu === "dau_ngay") return { kieu: "dau_ngay" };
  if (v.kieu === "n_ngay") { const n = soNguyen(v.n); return n >= GIOI_HAN_TAN_SUAT.nToiThieu && n <= GIOI_HAN_TAN_SUAT.nToiDa ? { kieu: "n_ngay", n } : "n"; }
  return null;
}
const opsCua = (banDo: BanDoPhanTich | undefined, nenTang: string): readonly ThaoTacPhanTich[] | undefined => banDo?.nenTang.find((n) => n.ma === nenTang)?.thaoTac;

interface Duong { id: string; nut: NutDoThi[] }

/** Tách đồ thị (đã đúng kiểu) thành các ĐƯỜNG theo thứ tự TẠO: đường xếp theo vị trí SỚM NHẤT trong nut[] của các nút thuộc đường; trong đường theo CẠNH (không theo vị trí, không theo x/y).
 *  Kiểm cấu trúc: cạnh trỏ nút lạ, tự nối, nút vào/ra > 1, vòng. Hỏng → {duong: [], loi}. */
function tachDuong(dt: Pick<DoThi, "nut" | "canh">): { duong: Duong[]; loi: string[] } {
  const loi: string[] = [];
  const nut = dt.nut;
  const chiSo = new Map(nut.map((n, i) => [n.id, i] as const));
  const tiep = new Map<string, string>(), truoc = new Map<string, string>();
  const ra = new Map<string, number>(), vao = new Map<string, number>();
  let canh: CanhDoThi[];
  if (dt.canh === undefined) canh = nut.slice(1).map((n, i) => ({ tu: nut[i]!.id, den: n.id }));
  else canh = dt.canh;
  canh.forEach((c, i) => {
    const so = i + 1;
    if (!chiSo.has(c.tu)) { loi.push(`Cạnh ${so}: không có nút “${c.tu}”`); return; }
    if (!chiSo.has(c.den)) { loi.push(`Cạnh ${so}: không có nút “${c.den}”`); return; }
    if (c.tu === c.den) { loi.push(`Cạnh ${so}: nút “${c.tu}” nối với chính nó — thành vòng`); return; }
    ra.set(c.tu, (ra.get(c.tu) ?? 0) + 1);
    vao.set(c.den, (vao.get(c.den) ?? 0) + 1);
    tiep.set(c.tu, c.den);
    truoc.set(c.den, c.tu);
  });
  for (const n of nut) {
    if ((ra.get(n.id) ?? 0) > 1) loi.push(`Nút “${n.id}” có ${ra.get(n.id)} cạnh đi ra — mỗi nút chỉ nối tới MỘT nút kế tiếp (muốn rẽ nhánh hãy làm đường riêng)`);
    if ((vao.get(n.id) ?? 0) > 1) loi.push(`Nút “${n.id}” có ${vao.get(n.id)} cạnh đi vào — mỗi nút chỉ nhận từ MỘT nút trước (muốn gộp nhánh hãy làm đường riêng)`);
  }
  if (loi.length) return { duong: [], loi };
  const daThay = new Set<string>();
  const duong: { sao: number; ds: NutDoThi[] }[] = [];
  for (const n of nut) {
    if (truoc.has(n.id)) continue; // chỉ bắt đầu từ ĐẦU đường
    const ds: NutDoThi[] = [];
    for (let cur: string | undefined = n.id; cur !== undefined && !daThay.has(cur); cur = tiep.get(cur)) { daThay.add(cur); ds.push(nut[chiSo.get(cur)!]!); }
    duong.push({ sao: Math.min(...ds.map((x) => chiSo.get(x.id)!)), ds });
  }
  if (daThay.size < nut.length) {
    const con = nut.filter((n) => !daThay.has(n.id)).map((n) => `“${n.id}”`);
    return { duong: [], loi: [`Có vòng: các nút ${con.join(", ")} nối thành vòng — mỗi đường phải có nút đầu và nút cuối (muốn làm lại nhiều lần hãy dùng vòng lặp “Quay lại”)`] };
  }
  duong.sort((a, b) => a.sao - b.sao);
  return { duong: duong.map((d) => ({ id: `duong-${nut[d.sao]!.id}`, nut: d.ds })), loi: [] };
}

/** Các đường theo thứ tự biên dịch (nút theo CẠNH). Đồ thị hỏng → [] (không ném). */
export function topoSapXep(doThi: Pick<DoThi, "nut" | "canh">): NutDoThi[][] {
  return tachDuong(doThi).duong.map((d) => d.nut);
}

// ================= VÒNG LẶP: đọc + kiểm cấu trúc =================
const RE_GIO = /^(\d{1,2}):(\d{2})$/;
/** "HH:MM" (giờ VN) → chuẩn "HH:MM"; trống → undefined; sai → null. */
function docGio(v: unknown): string | undefined | null {
  if (v === undefined || v === null || v === "") return undefined;
  if (!laChuoi(v)) return null;
  const m = RE_GIO.exec(v.trim());
  if (!m) return null;
  const h = Number(m[1]), p = Number(m[2]);
  return h < 24 && p < 60 ? `${String(h).padStart(2, "0")}:${m[2]}` : null;
}
/** Số nguyên từ số hoặc chữ số ("3"); khác → NaN. */
const soNguyen = (v: unknown): number => (typeof v === "number" ? (Number.isInteger(v) ? v : NaN) : laChuoi(v) && /^\s*-?\d+\s*$/.test(v) ? Number(v) : NaN);

/** Đọc danh sách cạnh lặp (kiểu dữ liệu, ô có thật). Lỗi đẩy vào `loi`. */
function docCanhLap(vao: readonly unknown[], co: ReadonlySet<string>, nutDu: boolean, loi: string[]): CanhLap[] {
  const ra: CanhLap[] = [];
  vao.forEach((c, i) => {
    const so = i + 1;
    if (!laObject(c) || !laChuoi(c.tu) || !laChuoi(c.den)) { loi.push(`Vòng lặp ${so} không hợp lệ (cần “tu” và “den” là mã ô)`); return; }
    const truoc = loi.length;
    for (const id of [c.tu, c.den]) if (!co.has(id) && nutDu) loi.push(`Vòng lặp ${so}: không có ô “${id}”`);
    let soLan: number = GIOI_HAN_LAP.soLanMacDinh;
    if (c.soLan !== undefined && c.soLan !== null) {
      soLan = soNguyen(c.soLan);
      if (!(soLan >= GIOI_HAN_LAP.soLanToiThieu && soLan <= GIOI_HAN_LAP.soLanToiDa)) loi.push(`Vòng lặp ${so}: số lần lặp thêm phải là số nguyên ${GIOI_HAN_LAP.soLanToiThieu}–${GIOI_HAN_LAP.soLanToiDa} (đang là “${String(c.soLan)}”)`);
    }
    const gio = docGio(c.denGio);
    if (gio === null) loi.push(`Vòng lặp ${so}: “lặp tới giờ” phải có dạng HH:MM (00:00–23:59), đang là “${String(c.denGio)}”`);
    if (c.denHetNghi !== undefined && c.denHetNghi !== null && typeof c.denHetNghi !== "boolean") loi.push(`Vòng lặp ${so}: “lặp tới khi hết bước Nghỉ” phải là bật hoặc tắt (true/false)`);
    if (loi.length > truoc) return;
    ra.push({ tu: c.tu, den: c.den, kieu: "lap", soLan, ...(gio ? { denGio: gio } : {}), ...(c.denHetNghi === true ? { denHetNghi: true } : {}) });
  });
  return ra;
}
/** Đọc "lặp cả sơ đồ" — thiếu / null → undefined. */
function docLapCaSoDo(v: unknown, loi: string[]): LapCaSoDo | undefined {
  if (v === undefined || v === null) return undefined;
  if (!laObject(v)) { loi.push("Lặp cả sơ đồ không hợp lệ (cần số vòng và nghỉ giữa vòng)"); return undefined; }
  const soVong = soNguyen(v.soVong);
  const truoc = loi.length;
  if (!(soVong >= 1 && soVong <= GIOI_HAN_LAP.soVongToiDa)) loi.push(`Lặp cả sơ đồ: số vòng phải là số nguyên 1–${GIOI_HAN_LAP.soVongToiDa} (đang là “${String(v.soVong)}”)`);
  const giay = (x: unknown, mac: number): number => (x === undefined || x === null || x === "" ? mac : soNguyen(x));
  const tu = giay(v.nghiGiuaVongTuS, 0);
  const den = giay(v.nghiGiuaVongDenS, Number.isNaN(tu) ? 0 : tu);
  const max = GIOI_HAN_LAP.nghiGiuaVongToiDaS;
  if (!(tu >= 0 && tu <= max) || !(den >= 0 && den <= max)) loi.push(`Lặp cả sơ đồ: nghỉ giữa vòng phải là số giây nguyên 0–${max} (tối đa 6 giờ)`);
  else if (tu > den) loi.push("Lặp cả sơ đồ: nghỉ giữa vòng “từ” không được lớn hơn “đến”");
  const gio = docGio(v.denGio);
  if (gio === null) loi.push(`Lặp cả sơ đồ: “tới giờ” phải có dạng HH:MM (00:00–23:59), đang là “${String(v.denGio)}”`);
  if (loi.length > truoc) return undefined;
  return { soVong, nghiGiuaVongTuS: tu, nghiGiuaVongDenS: den, ...(gio ? { denGio: gio } : {}) };
}
/**
 * Kiểm cấu trúc vòng lặp trên các ĐƯỜNG đã tách: quay về ô đứng TRƯỚC trong CÙNG đường, mỗi ô xuất phát ≤ 1 vòng, các vòng lồng nhau hoặc nối tiếp (cắt chéo → lỗi).
 * Cảnh báo vàng: vòng không qua ô Nghỉ mà lặp thêm > 3 lần.
 */
function kiemCauTrucLap(duong: readonly Duong[], lap: readonly CanhLap[], laNghi: (n: NutDoThi) => boolean): { loi: string[]; canhBao: CanhBaoDoThi[] } {
  const loi: string[] = [];
  const canhBao: CanhBaoDoThi[] = [];
  const viTri = new Map<string, { d: number; i: number }>();
  duong.forEach((dg, d) => dg.nut.forEach((n, i) => viTri.set(n.id, { d, i })));
  const hopLe: { L: CanhLap; d: number; a: number; b: number }[] = [];
  const daCoTu = new Set<string>();
  lap.forEach((L, k) => {
    const so = k + 1;
    const p = viTri.get(L.tu), q = viTri.get(L.den);
    if (!p || !q) return;
    // Nhánh MỘT ô "lặp tới khi hết Nghỉ": ô duy nhất vừa là đầu vừa là cuối đường → quay về chính nó là hợp lệ (làm lại ô đó tới khi ô Nghỉ gán cho nhánh hết). Vòng đếm số lần thì không.
    if (L.tu === L.den && !L.denHetNghi) { loi.push(`Vòng lặp ${so}: ô “${L.tu}” lặp về chính nó — vòng phải quay về một ô đứng TRƯỚC ô “${L.tu}” trong cùng đường`); return; }
    if (p.d !== q.d) { loi.push(`Vòng lặp ${so} từ “${L.tu}” về “${L.den}”: hai ô thuộc hai đường khác nhau — vòng chỉ quay về một ô đứng trước trong CÙNG một đường`); return; }
    if (q.i > p.i) { loi.push(`Vòng lặp ${so} từ “${L.tu}” về “${L.den}”: ô “${L.den}” đứng SAU ô “${L.tu}” — vòng chỉ quay về một ô đứng trước (muốn làm tiếp thì nối thường)`); return; }
    if (daCoTu.has(L.tu)) { loi.push(`Ô “${L.tu}” đã có một vòng quay lại — mỗi ô chỉ có một vòng lặp đi ra (sửa số lần của vòng đó, hoặc làm vòng lồng từ ô khác)`); return; }
    daCoTu.add(L.tu);
    hopLe.push({ L, d: p.d, a: q.i, b: p.i });
  });
  for (let x = 0; x < hopLe.length; x++) for (let y = x + 1; y < hopLe.length; y++) {
    const u = hopLe[x]!, v = hopLe[y]!;
    if (u.d !== v.d) continue;
    const [m, n] = u.a <= v.a ? [u, v] : [v, u];
    if (m.a < n.a && n.a <= m.b && m.b < n.b) loi.push(`Hai vòng lặp cắt chéo nhau: “${m.L.tu}” → “${m.L.den}” và “${n.L.tu}” → “${n.L.den}” — các vòng phải lồng nhau hoặc nối tiếp nhau (vòng này nằm trọn trong vòng kia, hoặc vòng sau bắt đầu SAU ô cuối của vòng trước)`);
  }
  for (const h of hopLe) {
    const than = duong[h.d]!.nut.slice(h.a, h.b + 1);
    if (h.L.soLan > GIOI_HAN_LAP.soLanKhongNghi && !than.some(laNghi)) canhBao.push({ loai: "lap_khong_nghi", nutId: h.L.tu, lyDo: `Vòng lặp “${h.L.tu}” → “${h.L.den}” làm thêm ×${h.L.soLan} liền nhau, không qua ô Nghỉ nào — lặp liền không nghỉ dễ bị mạng xã hội hạn chế`, cachSua: `Thêm một ô Nghỉ vào trong vòng, hoặc giảm số lần lặp xuống ${GIOI_HAN_LAP.soLanKhongNghi} trở xuống` });
  }
  return { loi, canhBao };
}

/**
 * (đợt A) Nghĩa của "Dành cho" / tần suất / "lặp tới khi hết nghỉ" trên các ĐƯỜNG đã tách: danhCho chỉ ở ô Nghỉ, trỏ một ô CÓ THẬT thuộc đường KHÁC; tần suất không đặt trên ô Nghỉ;
 * vòng denHetNghi chỉ trên đường được ít nhất một ô Nghỉ gán.
 */
function kiemDanhCho(duong: readonly Duong[], lap: readonly CanhLap[], laNghi: (n: NutDoThi) => boolean): string[] {
  const loi: string[] = [];
  const duongCua = new Map<string, string>();
  for (const dg of duong) for (const n of dg.nut) duongCua.set(n.id, dg.id);
  const duocGan = new Set<string>();
  for (const dg of duong) for (const n of dg.nut) {
    if (n.tanSuat && laNghi(n)) loi.push(`Bước “${n.id}”: bước Nghỉ không đặt tần suất (tần suất chỉ dành cho bước làm việc) — bỏ tần suất ở bước này`);
    if (n.danhCho === undefined) continue;
    if (!laNghi(n)) { loi.push(`Bước “${n.id}”: chỉ bước Nghỉ mới “dành cho” một nhánh khác được — bỏ “dành cho” ở bước này`); continue; }
    const dich = duongCua.get(n.danhCho);
    if (!dich) { loi.push(`Bước Nghỉ “${n.id}” dành cho bước “${n.danhCho}” — không có bước này trong sơ đồ (chọn lại nhánh ở mục “Dành cho”)`); continue; }
    if (dich === dg.id) { loi.push(`Bước Nghỉ “${n.id}” không thể dành cho chính nhánh của nó — chọn một nhánh khác (nhánh sẽ chạy trong lúc bước Nghỉ này nghỉ)`); continue; }
    duocGan.add(dich);
  }
  for (const L of lap) {
    if (!L.denHetNghi) continue;
    const d = duongCua.get(L.tu);
    if (d && !duocGan.has(d)) loi.push(`Vòng lặp từ “${L.tu}” về “${L.den}” đặt “lặp tới khi hết bước Nghỉ”, nhưng nhánh này chưa được gán vào bước Nghỉ nào — chọn “Dành cho” ở một bước Nghỉ của nhánh khác, hoặc tắt “lặp tới khi hết bước Nghỉ”`);
  }
  return loi;
}

/**
 * (gộp vòng lặp × AI sửa đồ thị / tự sửa đoạn / áp lại gói, 07/10/2026) Lọc danh sách vòng lặp CŨ trên nút / cạnh MỚI: giữ (đúng thứ tự) vòng còn hợp lệ
 * (đủ ô, cùng đường, quay về ô đứng trước, không cắt chéo vòng đã giữ…), loại vòng còn lại — để nơi sửa sơ đồ bằng máy không làm MẤT vòng người dùng tạo, cũng không để
 * vòng "treo" làm hỏng lưu. Kiểm bằng chính chuanHoaDoThi: thêm một vòng mà số lỗi tăng ⇒ vòng đó hỏng. Có `tuyChon.banDo` thì kiểm cả chỗ tách đoạn vòng bắt buộc (tachDoan).
 */
export function locVongHopLe(dt: { nenTang: string; nut: readonly unknown[]; canh?: readonly { tu: string; den: string }[] | null }, lap: readonly CanhLap[] | undefined | null, tuyChon: Partial<TuyChonTach> = {}): { lap: CanhLap[]; boQua: CanhLap[] } {
  const ra: CanhLap[] = [];
  const boQua: CanhLap[] = [];
  if (!lap?.length) return { lap: ra, boQua };
  const goc = { nenTang: dt.nenTang, nut: dt.nut, ...(dt.canh ? { canh: dt.canh } : {}) };
  const co = new Set(dt.nut.map((n) => (laObject(n) && laChuoi(n.id) ? n.id : "")));
  const loiGoc = chuanHoaDoThi(goc, tuyChon).loi.length;
  for (const c of lap) {
    if (!co.has(c.tu) || !co.has(c.den) || chuanHoaDoThi({ ...goc, lap: [...ra, c] }, tuyChon).loi.length > loiGoc) { boQua.push(c); continue; }
    ra.push(c);
  }
  return { lap: ra, boQua };
}

/**
 * Chuẩn hoá + kiểm hợp lệ. Không ném: sai → doThi = null + loi (tiếng Việt, cứng); canhBao = điều đáng chú ý nhưng không chặn.
 * Có `tuyChon.banDo` thì kiểm thêm mạng / thao tác có trong bản đồ, và trần theo ĐOẠN (đoạn ≤ 20 nút, ≤ 40 đoạn) — sau khi cắt (tachDoan), kèm cảnh báo không cắt được.
 * Không có `canh` (khoá thiếu) = chuỗi theo thứ tự nut[]; `canh: []` = mỗi nút một đường. Trường lạ (x, y, nhãn…) bị bỏ; số trong tham số đổi thành chữ.
 */
export function chuanHoaDoThi(x: unknown, tuyChon: Partial<TuyChonTach> = {}): { doThi: DoThi | null; loi: string[]; canhBao: CanhBaoDoThi[] } {
  const loi: string[] = [];
  const canhBao: CanhBaoDoThi[] = [];
  if (!laObject(x)) return { doThi: null, loi: ["Đồ thị không hợp lệ (phải là một object)"], canhBao };
  const nutVao = Array.isArray(x.nut) ? (x.nut as unknown[]) : [];
  if (!nutVao.length) loi.push("Đồ thị cần ít nhất 1 nút");
  if (nutVao.length > GIOI_HAN_DO_THI.nut) loi.push(`Đồ thị tối đa ${GIOI_HAN_DO_THI.nut} nút (đang có ${nutVao.length})`);
  const nut: NutDoThi[] = [];
  const idDaCo = new Set<string>();
  nutVao.forEach((n0, i) => {
    if (!laObject(n0)) { loi.push(`Nút ${i + 1} không hợp lệ`); return; }
    if (!idHopLe(n0.id)) { loi.push(`Mã nút của nút ${i + 1} không hợp lệ (chữ không rỗng, tối đa ${DO_DAI_ID_TOI_DA} ký tự, không có ký tự điều khiển)`); return; }
    const id = n0.id;
    const truocLoi = loi.length;
    if (idDaCo.has(id)) loi.push(`Mã nút “${id}” bị trùng — mỗi nút một mã riêng`);
    idDaCo.add(id);
    if (!laChuoi(n0.thaoTac) || !n0.thaoTac) loi.push(`Nút “${id}”: thiếu thao tác`);
    const ts: Record<string, string> = {};
    if (n0.thamSo !== undefined && n0.thamSo !== null) {
      if (!laObject(n0.thamSo)) loi.push(`Nút “${id}”: tham số không hợp lệ`);
      else for (const [k, v] of Object.entries(n0.thamSo)) {
        if (laChuoi(v)) ts[k] = v;
        else if (typeof v === "number" && Number.isFinite(v)) ts[k] = String(v);
        else if (v === undefined || v === null) ts[k] = "";
        else loi.push(`Nút “${id}”: tham số “${k}” phải là chữ hoặc số`);
      }
    }
    let nenTang: string | undefined;
    if (n0.nenTang !== undefined && n0.nenTang !== null) {
      if (!laChuoi(n0.nenTang) || !n0.nenTang.trim()) loi.push(`Nút “${id}”: mạng không hợp lệ`);
      else nenTang = n0.nenTang;
    }
    let nhuong: boolean | undefined;
    if (n0.nhuong !== undefined && n0.nhuong !== null) {
      if (typeof n0.nhuong !== "boolean") loi.push(`Nút “${id}”: “nhuong” (nhường lượt) phải là bật hoặc tắt (true/false)`);
      else nhuong = n0.nhuong;
    }
    if (n0.tuDuyet !== undefined && n0.tuDuyet !== null && typeof n0.tuDuyet !== "boolean") loi.push(`Nút “${id}”: “tự động” phải là bật hoặc tắt (true/false)`);
    // (đợt A) ô Nghỉ "Dành cho" + tần suất của ô việc — kiểu ở đây, nghĩa (ô Nghỉ / đường khác) kiểm sau khi tách đường
    let danhCho: string | undefined;
    if (n0.danhCho !== undefined && n0.danhCho !== null && n0.danhCho !== "") {
      if (!idHopLe(n0.danhCho)) loi.push(`Bước “${id}”: “dành cho” phải là mã một bước (chữ) thuộc nhánh được gán`);
      else danhCho = n0.danhCho;
    }
    const tanSuat = docTanSuat(n0.tanSuat);
    if (tanSuat === null) loi.push(`Bước “${id}”: Tần suất không hợp lệ (chọn “chỉ lần đầu mỗi ngày” hoặc “cứ N ngày”)`);
    else if (tanSuat === "n") loi.push(`Bước “${id}”: tần suất “cứ N ngày” cần N là số nguyên ${GIOI_HAN_TAN_SUAT.nToiThieu}–${GIOI_HAN_TAN_SUAT.nToiDa} (đang là “${String((n0.tanSuat as { n?: unknown }).n)}”)`);
    if (loi.length > truocLoi) return;
    nut.push({ id, thaoTac: n0.thaoTac as string, thamSo: ts, ...(n0.tuDuyet === true ? { tuDuyet: true } : {}), ...(nenTang !== undefined ? { nenTang } : {}), ...(nhuong !== undefined ? { nhuong } : {}), ...(danhCho !== undefined ? { danhCho } : {}), ...(tanSuat && typeof tanSuat === "object" ? { tanSuat } : {}) });
  });

  // mạng mặc định: khai, hoặc suy ra khi MỌI nút tự khai mạng
  let nenTangMacDinh = "";
  if (x.nenTang !== undefined && x.nenTang !== null && x.nenTang !== "") {
    if (!laChuoi(x.nenTang)) loi.push("Mạng mặc định của đồ thị phải là chữ");
    else nenTangMacDinh = x.nenTang;
  } else if (nut.length && nut.every((n) => n.nenTang)) nenTangMacDinh = nut[0]!.nenTang!;
  else if (nutVao.length) loi.push("Đồ thị cần mạng mặc định (nenTang), hoặc mọi nút tự khai mạng của mình");

  // cạnh (cạnh kieu:"lap" gửi lẫn trong `canh` được tách sang danh sách vòng lặp)
  let canh: CanhDoThi[] | undefined;
  const lapVao: unknown[] = [];
  if (x.canh !== undefined && x.canh !== null) {
    if (!Array.isArray(x.canh)) loi.push("Danh sách cạnh phải là danh sách (mảng)");
    else {
      canh = [];
      x.canh.forEach((c, i) => {
        if (laObject(c) && c.kieu === "lap") { lapVao.push(c); return; }
        if (!laObject(c) || !laChuoi(c.tu) || !laChuoi(c.den)) { loi.push(`Cạnh ${i + 1} không hợp lệ (cần “tu” và “den” là mã nút)`); return; }
        canh!.push({ tu: c.tu, den: c.den });
      });
    }
  }
  // vòng lặp + lặp cả sơ đồ (kiểu dữ liệu; cấu trúc kiểm sau khi tách đường)
  if (x.lap !== undefined && x.lap !== null) {
    if (!Array.isArray(x.lap)) loi.push("Danh sách vòng lặp phải là danh sách (mảng)");
    else lapVao.unshift(...x.lap);
  }
  const lap = docCanhLap(lapVao, new Set(nut.map((n) => n.id)), nutVao.length === nut.length, loi);
  const lapCaSoDo = docLapCaSoDo(x.lapCaSoDo, loi);
  if (lapCaSoDo && lapCaSoDo.soVong > GIOI_HAN_LAP.soLanKhongNghi && lapCaSoDo.nghiGiuaVongDenS === 0) canhBao.push({ loai: "lap_ca_so_do_khong_nghi", lyDo: `Lặp cả sơ đồ ×${lapCaSoDo.soVong} mà không nghỉ giữa vòng — lặp liền không nghỉ dễ bị mạng xã hội hạn chế`, cachSua: "Đặt nghỉ giữa vòng (ví dụ 30–60 phút), hoặc giảm số vòng xuống 3 trở xuống" });

  const doThi: DoThi = { nenTang: nenTangMacDinh, nut, ...(canh !== undefined ? { canh } : {}), ...(lap.length ? { lap } : {}), ...(lapCaSoDo ? { lapCaSoDo } : {}) };
  // cấu trúc đường (chỉ khi từng nút / cạnh đã đúng kiểu)
  if (!loi.length) {
    const d = tachDuong(doThi);
    loi.push(...d.loi);
    if (!d.loi.length) {
      if (d.duong.length > GIOI_HAN_DO_THI.duong) loi.push(`Đồ thị tối đa ${GIOI_HAN_DO_THI.duong} đường (đang có ${d.duong.length}) — gộp bớt các nút rời thành một đường`);
      if (canh !== undefined && nut.length > 1) for (const dg of d.duong) if (dg.nut.length === 1) canhBao.push({ loai: "nut_chua_noi", nutId: dg.nut[0]!.id, lyDo: `Nút “${dg.nut[0]!.id}” chưa nối với nút nào — thành một đường riêng chỉ có một nút`, cachSua: "Nối nút này vào đường cần chạy tiếp, hoặc để nguyên nếu muốn nó chạy riêng" });
      const laNghiN = (n: NutDoThi): boolean => { const op = opsCua(tuyChon.banDo, n.nenTang ?? doThi.nenTang)?.find((t) => t.ma === n.thaoTac); return op ? !!op.thuTu.nghi : n.thaoTac === "nghi"; };
      if (lap.length) {
        const k = kiemCauTrucLap(d.duong, lap, laNghiN);
        loi.push(...k.loi);
        canhBao.push(...k.canhBao);
      }
      loi.push(...kiemDanhCho(d.duong, lap, laNghiN));
    }
  }
  // theo bản đồ: mạng / thao tác có thật; tách đoạn kiểm trần đoạn + cảnh báo không cắt được
  if (!loi.length && tuyChon.banDo) {
    const banDo = tuyChon.banDo;
    const mangLa = new Set<string>();
    for (const n of nut) {
      const mang = n.nenTang ?? doThi.nenTang;
      const ops = opsCua(banDo, mang);
      if (!ops) { if (!mangLa.has(mang)) { mangLa.add(mang); loi.push(`Mạng “${mang}” chưa có trong bản đồ — hiện có: ${banDo.nenTang.map((m) => m.ma).join(", ")}`); } continue; }
      if (!ops.some((t) => t.ma === n.thaoTac)) loi.push(`Nút “${n.id}”: thao tác “${n.thaoTac}” không có trong bản đồ ${mang} — chỉ được dùng: ${ops.map((t) => t.ma).join(", ")}`);
    }
    if (!loi.length) {
      const tc = { ...tuyChon, banDo };
      const deXuat: DeXuatLan[] = [];
      let t = tachDoanTrong(doThi, tc, deXuat);
      // (08/10/2026) bước có tần suất cần dữ liệu của bước ngay trước → bước trước nhận cùng tần suất (sửa thẳng nút đã chuẩn hoá — lưu / canvas nhận lại bản này) + câu vàng
      if (deXuat.length) {
        const l = lanTanSuatNguoc(doThi, tc);
        if (l.lan.length) {
          for (const x of l.lan) { const n = nut.find((y) => y.id === x.nutId); if (n) n.tanSuat = { ...x.tanSuat }; }
          canhBao.push(...l.canhBao);
          t = tachDoanTrong(doThi, tc);
        }
      }
      loi.push(...t.loi);
      canhBao.push(...t.canhBao);
    }
  }
  const loiDuy = [...new Set(loi)];
  return { doThi: loiDuy.length ? null : doThi, loi: loiDuy, canhBao };
}

// ================= PHÂN TÍCH MỘT ĐOẠN (dùng lại luật có sẵn — KHÔNG viết lại luật) =================
interface PhanTichDoan { muc: MucThuTu[]; cach: (CachTuTim | null)[]; tuKhoa: string[]; ops: readonly ThaoTacPhanTich[] }
/** Khối hiệu lực + cách tìm + từ khoá tự tìm của một dãy nút MỘT mạng: gọi mucThuTuHieuLuc / cachTuTimKeHoach / tuKhoaTuTim của so-do-thu-tu.ts (cùng cách chuanHoaSoDo / dichSoDo dùng). null = có thao tác lạ. */
function phanTichDoan(nut: readonly NutDoThi[], ops: readonly ThaoTacPhanTich[], hoSoTen?: string | null, mucTieu?: string | null): PhanTichDoan | null {
  const tt = nut.map((n) => ops.find((t) => t.ma === n.thaoTac));
  if (tt.some((t) => !t)) return null;
  const ts = nut.map((n, j) => Object.fromEntries(tt[j]!.thamSo.map((d) => [d.ten, n.thamSo[d.ten] ?? d.macDinh ?? ""])));
  const tuKhoa = nut.map((_n, j) => tuKhoaTuTim(nut, j, hoSoTen, mucTieu).tuKhoa);
  const cach = cachTuTimKeHoach(tt.map((t, j) => ({ tuTim: t!.thuTuTuTim, ts: ts[j]!, coTuKhoa: !!tuKhoa[j], ma: t!.ma })));
  return { muc: mucThuTuHieuLuc(nut, ops, hoSoTen, mucTieu), cach, tuKhoa, ops };
}
const tenCua = (ops: readonly ThaoTacPhanTich[], n: NutDoThi): string => ops.find((t) => t.ma === n.thaoTac)?.tenNgan ?? n.thaoTac;
const tenCach = (c: CachTuTim | null): string => (c === null ? "bản chính (có địa chỉ)" : c === "day_du" ? "tự tìm đầy đủ" : c === "chi_lam" ? "dùng lại danh sách tìm của bước trước" : "chưa có từ khoá");

/**
 * Cắt tại ô Nghỉ R[i] (cả cụm Nghỉ liền R[i..den]) có AN TOÀN không — R = các nút liền nhau MỘT mạng từ chỗ cắt trước đến hết đoạn; A = R trước ô Nghỉ, B = R sau cụm Nghỉ.
 * AN TOÀN = ngữ nghĩa của A và B đứng riêng (hai bundle) giống hệt trong R (một bundle). Trả null nếu an toàn, ngược lại lý do + cách sửa. Luật (gọi các hàm có sẵn của so-do-thu-tu.ts, KHÔNG viết lại):
 *  (a) loiCanDanhSach(B) = ∅ — nút for_each phải có nguồn tạo danh sách NGAY trong B;
 *  (b) cachTuTimKeHoach giống nhau ở từng nút của A (trong A so với trong R) và của B ("chi_lam" dùng lại danh sách tìm ở bước TRƯỚC qua khoá `chung` — cắt giữa Tìm video và Thích tự tìm
 *      sẽ âm thầm thành "day_du"); mucThuTuHieuLuc (khối hiệu lực: id bước, danh sách tạo / cần) giống nhau;
 *  (c) tuKhoaTuTim giống nhau — chỉ xét nút DÙNG biến thể tự tìm (nút có địa chỉ không đọc từ khoá);
 *  (d) AI chấm liên quan: có mục tiêu, không sản phẩm → khối chấm chỉ chạy khi kịch bản CÓ bước tìm (sinhKichBan: coTimKiem); bên nào có khối chấm thì "có bước tìm" của bên đó phải bằng của R;
 *  (f) bước chèn chéo của sinhKichBan (xemRoiLam / L-71): khối ở A có VÒNG XEM trên danh sách D mà khối ở B làm việc trên D (chưa lấy lại D) — máy chèn lọc / chấm vào trước vòng xem của A;
 *  (e) khối KHÔNG khép kín (ThaoTacPhanTich.phuThuocNgoai: đọc biến / tham số do khối khác tạo / khai): mất giá trị nếu khối cung cấp nằm ở A mà B không có khối cung cấp nào trước nút đọc.
 *      Thiếu thông tin khép kín (bản đồ công khai cũ) → không cắt.
 *      LƯU Ý (đã kiểm mã nguồn): trong MỘT kịch bản có hai khối Tìm nhóm, khối "Tham gia nhóm" lần 2 đọc tham số `tu_khoa_nhom` của khối Tìm nhóm ĐẦU (lần n ≥ 2 chỉ đổi tên tham số mà khối
 *      TỰ khai — khoi-lan-n.ts), nên lọc nhóm lạc đề theo từ khoá lần 1. Cắt tại Nghỉ cho "Tham gia" lần 2 đọc từ khoá của Tìm nhóm CÙNG ĐOẠN (đúng ý người dùng) — canCat coi đây là cắt được.
 */
export function kiemCat(R: readonly NutDoThi[], i: number, tuyChon: BoiCanhDoThi & { nenTang: string; phuongAn?: string }, den: number = i): CanhBaoCat | null {
  const nghi = R[i];
  const nutId = nghi?.id ?? "";
  const khong = (loai: LoaiKhongCat, lyDo: string, cachSua: string, nutAnhHuong?: string): CanhBaoCat => ({ loai, nutId, ...(nutAnhHuong !== undefined ? { nutAnhHuong } : {}), lyDo, cachSua });
  // den = i − 1: cắt NGAY trước R[i] không có ô Nghỉ nào (biên của vòng lặp — tachDoan); A = R[0..i), B = R[i..]
  if (!nghi || i < 0 || den < i - 1 || den >= R.length) return khong("khong_phan_tich_duoc", "Vị trí ô Nghỉ không hợp lệ trong đoạn", "Báo người quản trị");
  const ops = opsCua(tuyChon.banDo, tuyChon.nenTang);
  if (!ops) return khong("khong_phan_tich_duoc", `Mạng “${tuyChon.nenTang}” chưa có trong bản đồ — không phân tích được nên không cắt`, "Dùng mạng có trong bản đồ");
  const { hoSoTen, mucTieu } = tuyChon;
  const A = R.slice(0, i), B = R.slice(den + 1);
  const pR = phanTichDoan(R, ops, hoSoTen, mucTieu), pA = phanTichDoan(A, ops, hoSoTen, mucTieu), pB = phanTichDoan(B, ops, hoSoTen, mucTieu);
  if (!pR || !pA || !pB) {
    const la = R.find((n) => !ops.some((t) => t.ma === n.thaoTac));
    return khong("khong_phan_tich_duoc", `Thao tác “${la?.thaoTac ?? "?"}” lạ (không có trong bản đồ) — không phân tích được nên không cắt`, "Đổi sang thao tác có trong bản đồ", la?.id);
  }
  const tatCa = ops.map((t) => ({ tenNgan: t.tenNgan, taoDs: t.thuTu.taoDs }));
  const phuongAn = tuyChon.phuongAn ?? "hoặc tắt “Nhường lượt” ở ô Nghỉ này (đoạn sẽ dài hơn nhưng vẫn đúng)";

  // (a) danh sách cần dùng
  const loiDs = loiCanDanhSach(pB.muc, tatCa);
  if (loiDs.length) {
    const j = Number(/^Bước (\d+)/.exec(loiDs[0]!)?.[1] ?? 1) - 1;
    const nguon = /từ bước “(.+?)” đặt/.exec(loiDs[0]!)?.[1] ?? "tìm";
    const b = B[j] ?? B[0]!;
    return khong("can_danh_sach", `Bước “${tenCua(ops, b)}” cần danh sách do bước “${nguon}” tạo ra ở TRƯỚC ô Nghỉ này — tách ở đây thì bước này không còn danh sách để làm`, `Đặt lại bước “${nguon}” SAU ô Nghỉ này, ngay trước bước “${tenCua(ops, b)}”; ${phuongAn}`, b.id);
  }
  // (b) cách tìm (A trong A so với trong R; B trong B so với trong R)
  const soCach = (nut: readonly NutDoThi[], p: PhanTichDoan, batDauR: number): CanhBaoCat | null => {
    for (let k = 0; k < nut.length; k++) {
      const cr = pR.cach[batDauR + k] ?? null;
      if (cr === p.cach[k]) continue;
      const n = nut[k]!;
      const ten = tenCua(ops, n);
      return khong("dung_lai_danh_sach", `Bước “${ten}” để trống địa chỉ nên tự tìm: trong cả đường nó theo cách “${tenCach(cr)}”, nhưng tách ở đây thì thành “${tenCach(p.cach[k] ?? null)}” — dùng lại danh sách tìm của bước trước thành tìm lại từ đầu (hoặc ngược lại), bot làm khác ý`, `Điền địa chỉ bài/video cụ thể cho bước “${ten}”, hoặc đặt bước Tìm và bước này cùng một đoạn (không có Nghỉ chen giữa); ${phuongAn}`, n.id);
    }
    return null;
  };
  const cA = soCach(A, pA, 0);
  if (cA) return cA;
  const cB = soCach(B, pB, den + 1);
  if (cB) return cB;
  // (c) từ khoá tự tìm — chỉ nút dùng biến thể tự tìm
  const soTuKhoa = (nut: readonly NutDoThi[], p: PhanTichDoan, batDauR: number): CanhBaoCat | null => {
    for (let k = 0; k < nut.length; k++) {
      if (p.cach[k] === null) continue;
      const tr = pR.tuKhoa[batDauR + k] ?? "", ca = p.tuKhoa[k] ?? "";
      if (tr === ca) continue;
      const n = nut[k]!;
      const ten = tenCua(ops, n);
      return khong("tu_khoa_tu_tim", `Bước “${ten}” để trống địa chỉ nên tự tìm theo từ khoá: trong cả đường là ${tr ? `“${tr}”` : "(chưa có)"}, nhưng tách ở đây thì thành ${ca ? `“${ca}”` : "(chưa có)"} — bot sẽ tìm khác ý`, `Ghi địa chỉ bài/video cụ thể cho bước “${ten}”, hoặc đặt bước Tìm (có từ khoá) cùng đoạn với nó; ${phuongAn}`, n.id);
    }
    return null;
  };
  const kA = soTuKhoa(A, pA, 0);
  if (kA) return kA;
  const kB = soTuKhoa(B, pB, den + 1);
  if (kB) return kB;
  // cấu trúc khối hiệu lực (id bước, danh sách tạo / cần, nghỉ, địa chỉ đích) — lưới an toàn cho mọi khác biệt còn lại
  const dang = (m: MucThuTu[]): string => JSON.stringify(m);
  if (dang(pA.muc) !== dang(pR.muc.slice(0, i))) return khong("doi_cau_truc", "Đoạn trước ô Nghỉ lắp ra khác khi tách ra", phuongAn);
  if (dang(pB.muc) !== dang(pR.muc.slice(den + 1))) return khong("doi_cau_truc", "Đoạn sau ô Nghỉ lắp ra khác khi tách ra", phuongAn);
  // các luật dưới cần thông tin "khép kín" của MỌI thao tác trong đoạn (bản đồ công khai cũ không có → không đoán, không cắt)
  const thieu = R.find((n) => { const t = ops.find((x) => x.ma === n.thaoTac)!; return !t.phuThuocNgoai || !t.taoBien || !t.khaiThamSo || !t.vongXem || !t.lamTrenDs || t.coChamPhuHop === undefined; });
  if (thieu) return khong("khoi_khong_khep_kin", `Bản đồ chưa có thông tin “khối khép kín” của bước “${tenCua(ops, thieu)}” — không biết bước trước / sau ô Nghỉ có dùng chung biến, danh sách hay không nên không cắt`, "Dùng bản đồ đầy đủ (banDoPhanTich) khi phân tích", thieu.id);
  const opCua = (n: NutDoThi): ThaoTacPhanTich => ops.find((x) => x.ma === n.thaoTac)!;
  // (f) bước chèn chéo của sinhKichBan (xemRoiLam / L-71): khối ở A có VÒNG XEM trên danh sách D (không được lấy lại D về sau trong A), khối ở B làm việc trên D mà trước nó (trong B) chưa lấy lại D
  //     → khi lắp chung máy chèn bước lọc / chấm vào TRƯỚC vòng xem của A (và "chỉ giữ mục đã xem" cho B); tách ra thì các bước chèn đó biến mất.
  // (khối dùng biến thể tự tìm → xét phần của biến thể; có địa chỉ → bản chính)
  const vongCua = (n: NutDoThi, cach: CachTuTim | null): readonly string[] => (cach === null ? opCua(n).vongXem! : (opCua(n).vongXemTuTim ?? opCua(n).vongXem!));
  const lamCua = (n: NutDoThi, cach: CachTuTim | null): readonly string[] => (cach === null ? opCua(n).lamTrenDs! : (opCua(n).lamTrenDsTuTim ?? opCua(n).lamTrenDs!));
  for (let ia = 0; ia < A.length; ia++) for (const ds of vongCua(A[ia]!, pA.cach[ia] ?? null)) {
    if (A.slice(ia + 1).some((n) => opCua(n).taoBien!.includes(ds))) continue;
    for (let k = 0; k < B.length; k++) {
      if (B.slice(0, k).some((n) => opCua(n).taoBien!.includes(ds))) break;
      // L-104 (07/10/2026): không lộ tên danh sách nội bộ (ds_video_tt…) — nói danh sách do bước nào lấy.
      const taoDs = A.slice(0, ia + 1).reverse().find((n) => opCua(n).taoBien!.includes(ds));
      const tenDs = taoDs ? `danh sách do bước “${tenCua(ops, taoDs)}” lấy` : "một danh sách lấy ở trước ô Nghỉ";
      if (lamCua(B[k]!, pB.cach[k] ?? null).includes(ds)) return khong("buoc_chen_cheo", `Bước “${tenCua(ops, A[ia]!)}” (trước ô Nghỉ) có vòng xem trên ${tenDs} và bước “${tenCua(ops, B[k]!)}” (sau ô Nghỉ) làm việc trên cùng danh sách — khi chạy chung máy chèn thêm bước lọc / chấm “liên quan” vào trước vòng xem để xem đúng mục sẽ làm; tách ra thì các bước chèn đó mất`, `Đặt hai bước này cùng đoạn (không có Nghỉ chen giữa), hoặc thêm một bước Tìm (lấy lại danh sách đó) ngay sau ô Nghỉ; ${phuongAn}`, B[k]!.id);
    }
  }
  // (d) AI chấm liên quan phụ thuộc "kịch bản có bước tìm"
  if (!(hoSoTen ?? "").trim() && (mucTieu ?? "").trim()) {
    const coTim = (nut: readonly NutDoThi[], p: PhanTichDoan): boolean => nut.some((n) => ops.find((t) => t.ma === n.thaoTac)?.thamSo.some((d) => d.ten === "tu_khoa")) || p.cach.some((c) => c !== null);
    const coCham = (nut: readonly NutDoThi[], p: PhanTichDoan): NutDoThi | undefined => nut.find((n, k) => { const t = opCua(n); return (p.cach[k] === null ? t.coChamPhuHop : (t.coChamPhuHopTuTim ?? t.coChamPhuHop)) === true; });
    const timR = coTim(R, pR);
    for (const [nut, p] of [[A, pA], [B, pB]] as const) {
      const c = coCham(nut, p);
      if (c && coTim(nut, p) !== timR) return khong("cham_lien_quan", `Có mục tiêu (không có sản phẩm) nên bước “${tenCua(ops, c)}” chỉ được AI lọc bài liên quan khi kịch bản CÓ bước tìm theo từ khoá; tách ở đây thì đoạn này ${timR ? "mất" : "có thêm"} bước tìm nên bộ lọc ${timR ? "tắt" : "bật"} — bot làm khác ý`, `Chọn Sản phẩm (AI luôn lọc), hoặc đặt bước Tìm cùng đoạn với bước này; ${phuongAn}`, c.id);
    }
  }
  // (e) khối không khép kín
  const cungCap = (n: NutDoThi, v: string): boolean => { const t = ops.find((x) => x.ma === n.thaoTac); return !!(t?.taoBien?.includes(v) || t?.khaiThamSo?.includes(v)); };
  for (let k = 0; k < B.length; k++) {
    const b = B[k]!;
    const t = opCua(b);
    for (const v of t.phuThuocNgoai!) {
      if (t.phuThuocMem?.includes(v)) continue; // (08/10/2026) đọc mềm, có đường lùi → không chặn cắt (tachDoan báo vàng — memMatKhiCat)
      // L-104 (07/10/2026): cờ vàng nói bằng chữ thường — TÊN bước tạo dữ liệu (“Tìm nhóm”), không lộ tên biến nội bộ (ds_nhom, tu_khoa_nhom).
      const tao = [...A].reverse().find((n) => cungCap(n, v));
      if (tao && !B.slice(0, k).some((n) => cungCap(n, v))) {
        const tenTao = tenCua(ops, tao);
        return khong("khoi_khong_khep_kin", `Bước “${t.tenNgan}” cần kết quả của bước “${tenTao}” đứng TRƯỚC ô Nghỉ này (bước “${t.tenNgan}” không tự lấy được) — tách ở đây thì nó mất kết quả đó`, `Thêm bước “${tenTao}” ngay sau ô Nghỉ, trước bước “${t.tenNgan}” (hoặc đặt hai bước này cùng đoạn, không có Nghỉ chen giữa), ${phuongAn}`, b.id);
      }
    }
  }
  return null;
}
/** Cắt được tại ô Nghỉ R[i] (cả cụm tới R[den]) mà không đổi ngữ nghĩa? (kiemCat === null) */
export function canCat(R: readonly NutDoThi[], i: number, tuyChon: BoiCanhDoThi & { nenTang: string }, den: number = i): boolean {
  return kiemCat(R, i, tuyChon, den) === null;
}

// ================= TÁCH ĐOẠN =================
export interface MucDoan { kieu: "doan"; doanId: string; nenTang: string; nutDau: string; nut: NutDoThi[]; tanSuat?: TanSuatNut }
/** (đợt A) `danhCho` = MÃ ĐƯỜNG được gán cho ô Nghỉ này (chỉ đường đó chạy trong lúc nghỉ). */
export interface MucNghi { kieu: "nghi"; tuS: number; denS: number; nutId: string; danhCho?: string }
/** Mục "QUAY LẠI" (vòng lặp): đặt ngay sau mục kết thúc ở ô `tu`; tới đây thì (còn lượt) quay về mục `veMuc` (bắt đầu ở ô `den`). Không phải việc của bot. (đợt A) `denHetNghi`: lặp tới khi hết ô Nghỉ đã gán. */
export interface MucLap { kieu: "lap"; veMuc: number; soLan: number; denGio?: string; tu: string; den: string; denHetNghi?: boolean }
export type MucTach = MucDoan | MucNghi | MucLap;
export interface DuongTach { id: string; nut: NutDoThi[]; muc: MucTach[] }
export interface KetQuaTach { duong: DuongTach[]; canhBao: CanhBaoDoThi[]; loi: string[] }

/** Khoảng nghỉ (giây) của một nút Nghỉ, đọc từ THAM SỐ của nút (thiếu → mặc định của bản đồ): Facebook tu_giay + den_giay; X / TikTok / YouTube / Reddit giay_nghi (một giá trị = tuS = denS). Không phải số → 0. tuS ≤ denS. */
export function khoangNghiS(n: NutDoThi, op: Pick<ThaoTacPhanTich, "thamSo"> | undefined): { tuS: number; denS: number } {
  const so = (g: string | undefined): number => { const s = (g ?? "").trim(); const v = s === "" ? NaN : Number(s); return Number.isFinite(v) && v >= 0 ? v : NaN; };
  const lay = (ten: string): number | undefined => {
    const d = op?.thamSo.find((x) => x.ten === ten);
    if (!d) return undefined;
    const v = so(n.thamSo[ten]);
    if (!Number.isNaN(v)) return v;
    const m = so(d.macDinh);
    // (đợt B) ô TUỲ CHỌN để trống, không có mặc định (tu_giay / den_giay mới của X / TikTok / YouTube / Reddit) → coi như không đặt (lùi về giay_nghi), không phải 0 giây
    return Number.isNaN(m) ? undefined : m;
  };
  const tu = lay("tu_giay"), den = lay("den_giay"), mot = lay("giay_nghi");
  // chỉ điền "từ" → "đến" = "từ" (như lúc lắp — tuy-chon-buoc.ts); chỉ điền "đến" → "từ" = 0; không điền ô nào → giay_nghi (một giá trị) → 0
  const a = tu ?? (den !== undefined ? 0 : mot ?? 0), b = den ?? (tu !== undefined ? tu : mot ?? a);
  return { tuS: Math.min(a, b), denS: Math.max(a, b) };
}

/**
 * Tách đồ thị thành ĐOẠN + mục NGHỈ cho từng đường (xem đầu tệp). `loi` = lỗi cứng (cấu trúc, mạng lạ, đoạn > 20 nút, > 40 đoạn); `canhBao` = chỗ định cắt mà không an toàn (không cắt).
 * Nghỉ sát chỗ đổi mạng luôn thành mục nghi; Nghỉ giữa đoạn chỉ thành mục nghi khi `nhuong` bật (cờ của nút thắng mặc định) và kiemCat cho phép; đường chỉ có Nghỉ → chỉ mục nghi (+ cảnh báo).
 */
export function tachDoan(doThi: DoThi, tuyChon: TuyChonTach): KetQuaTach {
  return tachDoanTrong(doThi, tuyChon);
}

/** (08/10/2026) Chỗ LAN NGƯỢC tần suất đề xuất được: bước `nut` (ngay trước, cùng chặng, không phải Nghỉ, chưa có tần suất) nên nhận tần suất `ts` của bước `tu` vì tách giữa hai bước không an toàn. */
interface DeXuatLan { nut: string; tu: string; ts: TanSuatNut; loai: LoaiKhongCat }
export interface LanTanSuat { nutId: string; tuNut: string; tanSuat: TanSuatNut }
const chuTanSuatNgan = (t: TanSuatNut): string => (t.kieu === "dau_ngay" ? "chỉ vòng đầu mỗi ngày" : `cứ ${t.n} ngày một lần`);
/**
 * (08/10/2026, lỗi thật: AI chỉ đặt "dau_ngay" ở Tham gia nhóm, không ở Tìm nhóm ngay trước → tách riêng bước có tần suất làm Tham gia mất danh sách → lỗi cứng.)
 * Bước có tần suất mà tách khỏi bước NGAY TRƯỚC không an toàn (kiemCat từ chối) → bước trước nhận CÙNG tần suất (hai bước gộp một đoạn), lặp tới khi chỗ tách an toàn.
 * Dừng ở: ô Nghỉ / chỗ cắt, đầu chặng (đổi mạng), bước đã có tần suất KHÁC, biên vòng lặp. Chỉ trong CÙNG một đường (không đụng nhánh khác). Không làm giảm số lỗi của tachDoan → không đổi gì.
 * Trả đồ thị mới (không sửa đầu vào), danh sách nút đã nhận tần suất và câu vàng (một câu cho mỗi bước gốc).
 */
export function lanTanSuatNguoc(doThi: DoThi, tuyChon: TuyChonTach): { doThi: DoThi; lan: LanTanSuat[]; canhBao: CanhBaoDoThi[] } {
  const khong = { doThi, lan: [] as LanTanSuat[], canhBao: [] as CanhBaoDoThi[] };
  let g = doThi;
  const goc = new Map<string, string>();
  const loaiGoc = new Map<string, LoaiKhongCat>();
  const lan: LanTanSuat[] = [];
  let loi0: number | null = null;
  for (let vong = 0; vong <= doThi.nut.length; vong++) {
    const thu: DeXuatLan[] = [];
    const t = tachDoanTrong(g, tuyChon, thu);
    if (loi0 === null) loi0 = t.loi.length;
    const moi = thu.filter((x) => !goc.has(x.nut) && !g.nut.find((n) => n.id === x.nut)?.tanSuat);
    if (!moi.length) break;
    const dat = new Map(moi.map((x) => [x.nut, x] as const));
    g = { ...g, nut: g.nut.map((n) => { const x = dat.get(n.id); return x ? { ...n, tanSuat: { ...x.ts } } : n; }) };
    for (const x of moi) {
      const o = goc.get(x.tu) ?? x.tu;
      goc.set(x.nut, o);
      if (!loaiGoc.has(o)) loaiGoc.set(o, x.loai);
      lan.push({ nutId: x.nut, tuNut: o, tanSuat: { ...x.ts } });
    }
  }
  if (!lan.length || tachDoanTrong(g, tuyChon).loi.length >= (loi0 ?? 0)) return khong;
  const ten = (id: string): string => { const n = g.nut.find((x) => x.id === id)!; return opsCua(tuyChon.banDo, n.nenTang ?? g.nenTang)?.find((t) => t.ma === n.thaoTac)?.tenNgan ?? n.thaoTac; };
  const thuTu = new Map(g.nut.map((n, i) => [n.id, i] as const));
  const canhBao: CanhBaoDoThi[] = [...loaiGoc.keys()].map((o) => {
    const ds = lan.filter((l) => l.tuNut === o).sort((a, b) => thuTu.get(a.nutId)! - thuTu.get(b.nutId)!);
    const cai = loaiGoc.get(o) === "can_danh_sach" ? "danh sách" : "kết quả";
    return {
      loai: "tan_suat_lan", nutId: ds[0]!.nutId, nutAnhHuong: o,
      lyDo: `Đã áp “${chuTanSuatNgan(ds[0]!.tanSuat)}” cho cả bước ${ds.map((l) => `“${ten(l.nutId)}”`).join(", ")} vì bước “${ten(o)}” cần ${cai} của ${ds.length > 1 ? "các bước này" : "nó"} (phải chạy cùng một đoạn)`,
      cachSua: `Không muốn vậy thì bỏ tần suất ở bước “${ten(o)}”`,
    };
  });
  return { doThi: g, lan, canhBao };
}

/** (08/10/2026) Cắt AN TOÀN nhưng bước sau chỗ cắt mất nguồn đọc MỀM (phuThuocMem) do bước trước chỗ cắt cung cấp → câu vàng (bot tự lùi về nguồn khác). */
function memMatKhiCat(R: readonly NutDoThi[], i: number, den: number, ops: readonly ThaoTacPhanTich[] | undefined): CanhBaoDoThi | null {
  if (!ops) return null;
  const A = R.slice(0, i), B = R.slice(den + 1);
  const opCua = (n: NutDoThi): ThaoTacPhanTich | undefined => ops.find((x) => x.ma === n.thaoTac);
  const cungCap = (n: NutDoThi, v: string): boolean => { const t = opCua(n); return !!(t?.taoBien?.includes(v) || t?.khaiThamSo?.includes(v)); };
  for (let k = 0; k < B.length; k++) {
    const t = opCua(B[k]!);
    for (const v of t?.phuThuocMem ?? []) {
      const tao = [...A].reverse().find((n) => cungCap(n, v));
      if (!tao || B.slice(0, k).some((n) => cungCap(n, v))) continue;
      const tenTao = tenCua(ops, tao);
      return { loai: "nguon_mem", nutId: B[k]!.id, nutAnhHuong: tao.id, lyDo: `Bước “${t!.tenNgan}” ở sau chỗ tách không dùng được kết quả của bước “${tenTao}” ở trước — bot sẽ tự chọn nhóm đã tham gia trong “Nhóm của bạn” khớp từ khoá (không có thì lướt trang chủ)`, cachSua: `Muốn lướt đúng nhóm vừa tìm: thêm bước “${tenTao}” ngay trước bước “${t!.tenNgan}”, hoặc để nguyên` };
    }
  }
  return null;
}

function tachDoanTrong(doThi: DoThi, tuyChon: TuyChonTach, deXuat?: DeXuatLan[]): KetQuaTach {
  const loi: string[] = [];
  const canhBao: CanhBaoDoThi[] = [];
  const d = tachDuong(doThi);
  if (d.loi.length) return { duong: [], canhBao, loi: d.loi };
  const nhuongMacDinh = tuyChon.nhuongMacDinh ?? d.duong.length >= 2;
  const mangLa = new Set<string>();
  let tongDoan = 0;
  const duongCuaNut = new Map<string, string>();
  for (const dg of d.duong) for (const n of dg.nut) duongCuaNut.set(n.id, dg.id);
  const ra: DuongTach[] = d.duong.map((dg, soDuong) => {
    const muc: MucTach[] = [];
    const mang = (n: NutDoThi): string => n.nenTang ?? doThi.nenTang;
    const opsMang = (m: string): readonly ThaoTacPhanTich[] | undefined => {
      const o = opsCua(tuyChon.banDo, m);
      if (!o && !mangLa.has(m)) { mangLa.add(m); loi.push(`Mạng “${m}” chưa có trong bản đồ`); }
      return o;
    };
    const laNghi = (n: NutDoThi): boolean => !!opsMang(mang(n))?.find((t) => t.ma === n.thaoTac)?.thuTu.nghi;
    const themNghi = (n: NutDoThi): void => {
      const k = khoangNghiS(n, opsMang(mang(n))?.find((t) => t.ma === n.thaoTac));
      const danh = n.danhCho !== undefined ? duongCuaNut.get(n.danhCho) : undefined;
      muc.push({ kieu: "nghi", tuS: k.tuS, denS: k.denS, nutId: n.id, ...(danh && danh !== dg.id ? { danhCho: danh } : {}) });
    };
    const themDoan = (m: string, ns: NutDoThi[]): void => {
      if (!ns.length) return;
      tongDoan++;
      if (ns.length > GIOI_HAN_DO_THI.nutMoiDoan) loi.push(`Đường ${soDuong + 1}: có đoạn ${ns.length} nút liền một mạng (từ nút “${ns[0]!.id}”), tối đa ${GIOI_HAN_DO_THI.nutMoiDoan} nút mỗi đoạn — thêm ô Nghỉ ở giữa (bật “Nhường lượt”) hoặc tách thành hai đường`);
      // (đợt B) đoạn mang tần suất khi MỌI ô của nó cùng một tần suất (một ô, hoặc các ô liền nhau cùng tần suất)
      const ts = ns[0]!.tanSuat && ns.every((x) => JSON.stringify(x.tanSuat ?? null) === JSON.stringify(ns[0]!.tanSuat)) ? ns[0]!.tanSuat : undefined;
      muc.push({ kieu: "doan", doanId: `doan-${ns[0]!.id}`, nenTang: m, nutDau: ns[0]!.id, nut: ns.map(saoNut), ...(ts ? { tanSuat: { ...ts } } : {}) });
    };
    const nut = dg.nut;
    // vòng lặp của đường này: ô quay về (den) phải BẮT ĐẦU một mục, ô xuất phát (tu) phải KẾT THÚC một mục → cắt đoạn đúng biên vòng
    const trongDuong = new Set(nut.map((n) => n.id));
    const lapDuong = (doThi.lap ?? []).filter((L) => trongDuong.has(L.tu) && trongDuong.has(L.den));
    const catTruoc = new Map(lapDuong.map((L) => [L.den, L] as const)), catSau = new Map(lapDuong.map((L) => [L.tu, L] as const));
    const xong = (): DuongTach => ({ id: dg.id, nut: nut.map(saoNut), muc: chenLap(muc, lapDuong, soDuong, loi) });
    if (nut.length && nut.every(laNghi)) {
      for (const n of nut) themNghi(n);
      canhBao.push({ loai: "duong_chi_co_nghi", nutId: nut[0]!.id, lyDo: `Đường ${soDuong + 1} chỉ có ô Nghỉ — không có việc thật nào để làm`, cachSua: "Thêm việc (tìm, xem, bình luận…) vào đường này hoặc bỏ nó" });
      return xong();
    }
    // các CHẶNG liền nhau cùng mạng
    const chang: { m: string; ns: NutDoThi[] }[] = [];
    for (const n of nut) { const m = mang(n); const cuoi = chang[chang.length - 1]; if (cuoi && cuoi.m === m) cuoi.ns.push(n); else chang.push({ m, ns: [n] }); }
    chang.forEach((c, r) => {
      const ops = opsMang(c.m);
      const ns = c.ns;
      const laNghiC = (n: NutDoThi): boolean => !!ops?.find((t) => t.ma === n.thaoTac)?.thuTu.nghi;
      let lo = 0, hi = ns.length;
      if (r > 0) while (lo < hi && laNghiC(ns[lo]!)) themNghi(ns[lo++]!); // Nghỉ sát chỗ đổi mạng (đầu chặng) → mục nghi
      const duoi: NutDoThi[] = [];
      if (r < chang.length - 1) while (hi > lo && laNghiC(ns[hi - 1]!)) duoi.unshift(ns[--hi]!); // (cuối chặng)
      let s = lo, i = lo;
      /** Biên vòng lặp ngay trước ns[k] (chưa là chỗ cắt) → cắt BẮT BUỘC; không an toàn → lỗi cứng (vòng chạy lại một phần đoạn sẽ làm bot làm khác ý). */
      const catVong = (k: number): void => {
        if (!(k > s)) return;
        const vong = catTruoc.has(ns[k]!.id) || catSau.has(ns[k - 1]!.id);
        // (đợt A) ô có TẦN SUẤT riêng là một đoạn riêng: cắt ngay trước và ngay sau nó. (đợt B) các ô LIỀN NHAU có CÙNG tần suất (vd Tìm nhóm + Tham gia nhóm "chỉ vòng đầu
        // mỗi ngày") gộp MỘT đoạn — bước làm cần danh sách của bước tìm ngay trước nên không được tách khỏi nhau.
        const ts = (n: NutDoThi): string => JSON.stringify(n.tanSuat ?? null);
        const nutTs = ts(ns[k]!) === ts(ns[k - 1]!) ? null : ns[k]!.tanSuat ? ns[k]! : ns[k - 1]!.tanSuat ? ns[k - 1]! : null;
        if (!vong && !nutTs) return;
        const doi = (x: string): string => x.replace(/ô Nghỉ này/g, "chỗ tách này").replace(/ô Nghỉ/g, "chỗ tách");
        if (vong) {
          const L = catTruoc.get(ns[k]!.id) ?? catSau.get(ns[k - 1]!.id)!;
          const phuongAn = "hoặc cho vòng quay về ô sớm hơn (gồm cả bước tạo danh sách / từ khoá), hoặc bỏ vòng lặp này";
          const cb = kiemCat(ns.slice(s, hi), k - s, { banDo: tuyChon.banDo, nenTang: c.m, hoSoTen: tuyChon.hoSoTen, mucTieu: tuyChon.mucTieu, phuongAn }, k - s - 1);
          if (cb) loi.push(`Vòng lặp từ “${L.tu}” quay lại “${L.den}” cần tách đoạn ngay trước bước “${tenCua(ops ?? [], ns[k]!)}”, nhưng tách ở đây bot sẽ làm khác ý: ${doi(cb.lyDo)} — Cách sửa: ${doi(cb.cachSua)}`);
          else { const m = memMatKhiCat(ns.slice(s, hi), k - s, k - s - 1, ops); if (m) canhBao.push({ ...m, lyDo: doi(m.lyDo) }); }
        } else if (nutTs) {
          const cb = kiemCat(ns.slice(s, hi), k - s, { banDo: tuyChon.banDo, nenTang: c.m, hoSoTen: tuyChon.hoSoTen, mucTieu: tuyChon.mucTieu, phuongAn: "hoặc bỏ tần suất riêng của bước này (bước chạy mỗi lần như các bước khác)" }, k - s - 1);
          if (cb) loi.push(`Bước “${tenCua(ops ?? [], nutTs)}” (bước “${nutTs.id}”) có tần suất riêng nên phải tách thành đoạn riêng, nhưng tách ở đây bot sẽ làm khác ý: ${doi(cb.lyDo)} — Cách sửa: ${doi(cb.cachSua)}`);
          else { const m = memMatKhiCat(ns.slice(s, hi), k - s, k - s - 1, ops); if (m) canhBao.push({ ...m, lyDo: doi(m.lyDo) }); }
          // (08/10/2026) tách không an toàn vì bước có tần suất cần dữ liệu của bước NGAY TRƯỚC (việc, chưa có tần suất) → đề xuất lan ngược tần suất (lanTanSuatNguoc)
          if (cb && deXuat && ns[k]!.tanSuat && !ns[k - 1]!.tanSuat && !laNghiC(ns[k - 1]!)) deXuat.push({ nut: ns[k - 1]!.id, tu: ns[k]!.id, ts: ns[k]!.tanSuat, loai: cb.loai });
        }
        themDoan(c.m, ns.slice(s, k));
        s = k;
      };
      while (i < hi) {
        if (!laNghiC(ns[i]!)) { catVong(i); i++; continue; }
        const p = i;
        let q = i;
        while (q + 1 < hi && laNghiC(ns[q + 1]!)) q++;
        i = q + 1;
        // (đợt A) ô Nghỉ "Dành cho" một đường khác LUÔN cắt (bot phải được nhả cho đường đó); cắt không an toàn → lỗi cứng có cách sửa (không tự chèn bước)
        const ganDanh = ns.slice(p, q + 1).find((n) => n.danhCho !== undefined);
        const bat = !!ganDanh || ns.slice(p, q + 1).some((n) => n.nhuong ?? nhuongMacDinh);
        const cb = bat ? kiemCat(ns.slice(s, hi), p - s, { banDo: tuyChon.banDo, nenTang: c.m, hoSoTen: tuyChon.hoSoTen, mucTieu: tuyChon.mucTieu, ...(ganDanh ? { phuongAn: "hoặc bỏ “Dành cho” ở bước Nghỉ này (khi đó đoạn không tách, bot không được nhả cho nhánh kia)" } : {}) }, q - s) : null;
        if (ganDanh && cb) loi.push(`Bước Nghỉ “${ganDanh.id}” dành cho nhánh khác nên phải tách đoạn để nhả bot, nhưng tách ở đây bot sẽ làm khác ý: ${cb.lyDo} — Cách sửa: ${cb.cachSua}`);
        if (bat && !cb) {
          const mm = memMatKhiCat(ns.slice(s, hi), p - s, q - s, ops);
          if (mm) canhBao.push(mm);
          themDoan(c.m, ns.slice(s, p));
          for (let k = p; k <= q; k++) themNghi(ns[k]!);
          s = q + 1;
          continue;
        }
        if (cb) canhBao.push(cb);
        for (let k = p; k <= q; k++) catVong(k); // cụm Nghỉ ở lại trong đoạn: biên vòng bên trong cụm vẫn phải cắt
      }
      themDoan(c.m, ns.slice(s, hi));
      for (const n of duoi) themNghi(n);
    });
    return xong();
  });
  if (tongDoan > GIOI_HAN_DO_THI.doanMoiLuot) loi.push(`Một lượt có ${tongDoan} đoạn, tối đa ${GIOI_HAN_DO_THI.doanMoiLuot} đoạn — bớt nút hoặc gộp các nút liền một mạng`);
  return { duong: ra, canhBao, loi: [...new Set(loi)] };
}

// ================= VÒNG LẶP TRONG KẾ HOẠCH =================
/** Chèn mục "lap" ngay sau mục kết thúc ở ô xuất phát của mỗi vòng; veMuc = chỉ số (SAU khi chèn) của mục bắt đầu ở ô quay về. Kiểm trần sau khi bung. */
function chenLap(muc: MucTach[], lap: readonly CanhLap[], soDuong: number, loi: string[]): MucTach[] {
  if (!lap.length) return muc;
  const dau = (m: MucTach): string => (m.kieu === "doan" ? m.nut[0]!.id : m.kieu === "nghi" ? m.nutId : "");
  const cuoi = (m: MucTach): string => (m.kieu === "doan" ? m.nut[m.nut.length - 1]!.id : m.kieu === "nghi" ? m.nutId : "");
  const theoCuoi = new Map<number, { L: CanhLap; a: number }>();
  for (const L of lap) {
    const a = muc.findIndex((m) => dau(m) === L.den), b = muc.findIndex((m) => cuoi(m) === L.tu);
    if (a < 0 || b < 0 || a > b) { loi.push(`Đường ${soDuong + 1}: không đặt được vòng lặp “${L.tu}” → “${L.den}” — hai ô này nằm giữa một đoạn không tách được`); continue; }
    theoCuoi.set(b, { L, a });
  }
  const ra: MucTach[] = [];
  const chiSoMoi: number[] = [];
  const cho: { m: MucLap; a: number }[] = [];
  muc.forEach((m, j) => {
    chiSoMoi[j] = ra.length;
    ra.push(m);
    const x = theoCuoi.get(j);
    if (!x) return;
    const ml: MucLap = { kieu: "lap", veMuc: -1, soLan: x.L.soLan, ...(x.L.denGio ? { denGio: x.L.denGio } : {}), tu: x.L.tu, den: x.L.den, ...(x.L.denHetNghi ? { denHetNghi: true } : {}) };
    cho.push({ m: ml, a: x.a });
    ra.push(ml);
  });
  for (const c of cho) c.m.veMuc = chiSoMoi[c.a]!;
  const tong = demSauBung(ra);
  if (tong > GIOI_HAN_LAP.mucSauBung) loi.push(`Đường ${soDuong + 1}: sau khi bung các vòng lặp có ${tong} mục (đoạn + nghỉ), tối đa ${GIOI_HAN_LAP.mucSauBung} — bớt số lần lặp hoặc bớt vòng lồng nhau`);
  return ra;
}
/** Số mục (đoạn + nghỉ) một đường chạy sau khi bung mọi vòng (không rút Nháp, không tính giờ dừng sớm). */
export function demSauBung(muc: readonly { kieu: string; veMuc?: number; soLan?: number }[]): number {
  let tong = 0;
  muc.forEach((m, j) => {
    if (m.kieu === "lap") return;
    let k = 1;
    for (let t = j + 1; t < muc.length; t++) { const x = muc[t]!; if (x.kieu === "lap" && (x.veMuc ?? Infinity) <= j) k *= (x.soLan ?? 0) + 1; }
    tong += k;
  });
  return tong;
}
export interface MucBung { chiSo: number; vong: { mucLap: number; lan: number; tong: number }[] }
/**
 * Bung vòng: thứ tự các mục (đoạn / nghỉ) một đường sẽ chạy — CÙNG thuật toán đếm của bộ điều phối (đếm số lần đã lặp theo mục "lap"; vòng ngoài quay lại thì đếm vòng trong làm lại từ đầu).
 * `vong` = các vòng đang chứa mục (ngoài → trong) với lần thứ mấy / tổng. Nháp rút còn ≤ 2 vòng. Không tính `denGio` (giờ chỉ biết lúc chạy).
 */
export function bungVong(muc: readonly MucKeHoach[], o: { nhap?: boolean } = {}): MucBung[] {
  const nhap = o.nhap === true;
  const dem = new Map<number, number>();
  const ra: MucBung[] = [];
  let i = 0;
  for (let guard = 0; i < muc.length && guard < 100_000; guard++) {
    const m = muc[i]!;
    if (m.kieu === "lap") {
      const c = dem.get(i) ?? 0;
      if (c + 1 < soVongLap(m.soLan, nhap) && m.veMuc >= 0 && m.veMuc < i) {
        dem.set(i, c + 1);
        for (const k of [...dem.keys()]) if (k >= m.veMuc && k < i) dem.delete(k);
        i = m.veMuc;
        continue;
      }
      i++;
      continue;
    }
    const vong: MucBung["vong"] = [];
    for (let k = muc.length - 1; k > i; k--) { const x = muc[k]!; if (x.kieu === "lap" && x.veMuc <= i) vong.push({ mucLap: k, lan: (dem.get(k) ?? 0) + 1, tong: soVongLap(x.soLan, nhap) }); }
    ra.push({ chiSo: i, vong });
    i++;
  }
  return ra;
}

// ================= KẾ HOẠCH LƯỢT (kiểu dùng chung với bộ điều phối) =================
export type MucKeHoach =
  | { kieu: "doan"; doanId: string; nutDau: string; nenTang: string; uocS?: number; tanSuat?: TanSuatNut }
  | { kieu: "nghi"; tuS: number; denS: number; nutId: string; danhCho?: string }
  | { kieu: "lap"; veMuc: number; soLan: number; denGio?: string; tu: string; den: string; denHetNghi?: boolean };
/** `lapCaSoDo` (có khi sơ đồ đặt Lặp cả sơ đồ): tổng số vòng + nghỉ giữa vòng (giây) + (đợt A) không bắt đầu vòng mới từ `denGio` (giờ VN). */
export interface KeHoachLuot { duong: { id: string; muc: MucKeHoach[] }[]; lapCaSoDo?: { soVong: number; tuS: number; denS: number; denGio?: string } }

/** Đồ thị → kế hoạch lượt. doanId = "doan-<id nút đầu của đoạn>" (ổn định: sửa tham số / thêm nút cuối / chèn nút giữa đoạn không đổi). Đồ thị sai → ném LoiDoThi (đủ danh sách lỗi). */
export function duKienKeHoach(doThi: unknown, tuyChon: TuyChonTach): KeHoachLuot {
  const r = chuanHoaDoThi(doThi, tuyChon);
  if (!r.doThi) throw new LoiDoThi(r.loi);
  const t = tachDoan(r.doThi, tuyChon);
  if (t.loi.length) throw new LoiDoThi(t.loi);
  const lc = r.doThi.lapCaSoDo;
  return {
    ...(lc ? { lapCaSoDo: { soVong: lc.soVong, tuS: lc.nghiGiuaVongTuS, denS: lc.nghiGiuaVongDenS, ...(lc.denGio ? { denGio: lc.denGio } : {}) } } : {}),
    duong: t.duong.map((d) => ({
      id: d.id,
      muc: d.muc.map((m): MucKeHoach => {
        if (m.kieu === "nghi") return { kieu: "nghi", tuS: m.tuS, denS: m.denS, nutId: m.nutId, ...(m.danhCho ? { danhCho: m.danhCho } : {}) };
        if (m.kieu === "lap") return { kieu: "lap", veMuc: m.veMuc, soLan: m.soLan, ...(m.denGio ? { denGio: m.denGio } : {}), tu: m.tu, den: m.den, ...(m.denHetNghi ? { denHetNghi: true } : {}) };
        const uoc = tuyChon.uocS?.(m);
        return { kieu: "doan", doanId: m.doanId, nutDau: m.nutDau, nenTang: m.nenTang, ...(uoc !== undefined ? { uocS: uoc } : {}), ...(m.tanSuat ? { tanSuat: { ...m.tanSuat } } : {}) };
      }),
    })),
  };
}

// ================= NHÃN "(lần n)" =================
export interface NhanLanNut { nutId: string; doanId?: string; lan: number; lanToanDo: number; nhan: string }
/**
 * Nhãn "Tìm nhóm (lần 2)" theo ĐOẠN: mỗi đoạn là MỘT bundle riêng nên "lần dùng thứ n" (hậu tố `_n` của id bước + tham số — lan-dung.ts / lanTheoNut) tính LẠI từ 1 ở mỗi đoạn; `nhan` dùng số này
 * (khớp ô tham số thật). `lanToanDo` = đây là lần thứ mấy cùng thao tác (cùng mạng) xuất hiện trên cả đồ thị theo thứ tự biên dịch — để hiển thị phụ. Nghỉ không đánh số trong `nhan`.
 * Thứ tự kết quả = thứ tự biên dịch (đường → mục). Đồ thị sai → [].
 */
export function danhNhanLan(doThi: DoThi, tuyChon: TuyChonTach): NhanLanNut[] {
  const t = tachDoan(doThi, tuyChon);
  if (t.loi.length) return [];
  const dem = new Map<string, number>();
  const ra: NhanLanNut[] = [];
  const nutTheoId = new Map(t.duong.flatMap((d) => d.nut).map((n) => [n.id, n] as const));
  /** Lần thứ mấy cùng thao tác (cùng mạng) xuất hiện trên cả đồ thị, theo thứ tự biên dịch. */
  const nutToanDo = (n: NutDoThi): { lanToanDo: number } => { const k = `${n.nenTang ?? doThi.nenTang}/${n.thaoTac}`; const v = (dem.get(k) ?? 0) + 1; dem.set(k, v); return { lanToanDo: v }; };
  for (const d of t.duong) for (const m of d.muc) {
    if (m.kieu === "lap") continue;
    if (m.kieu === "nghi") {
      const n = nutTheoId.get(m.nutId)!;
      const ten = tenCua(opsCua(tuyChon.banDo, n.nenTang ?? doThi.nenTang) ?? [], n);
      ra.push({ nutId: n.id, lan: 1, ...nutToanDo(n), nhan: ten });
      continue;
    }
    const ops = opsCua(tuyChon.banDo, m.nenTang) ?? [];
    const pt = phanTichDoan(m.nut, ops, tuyChon.hoSoTen, tuyChon.mucTieu);
    const lan = pt ? lanTheoNut(pt.muc) : m.nut.map(() => 1);
    m.nut.forEach((n, k) => {
      const ten = tenCua(ops, n);
      const nghi = !!ops.find((x) => x.ma === n.thaoTac)?.thuTu.nghi;
      ra.push({ nutId: n.id, doanId: m.doanId, lan: lan[k]!, ...nutToanDo(n), nhan: nghi ? ten : nhanLan(ten, lan[k]!) });
    });
  }
  return ra;
}

// ================= CẢNH BÁO THEO TỪNG ĐOẠN =================
export interface CanhBaoDoan { doanId: string; nutId?: string; loai: "can_danh_sach" | "to_hop" | "nghi_lien"; noi: string }
/** Chạy loiCanDanhSach / loiToHop / loiNghiLien THEO TỪNG ĐOẠN (không trên mảng chung): mỗi đoạn là một kịch bản — danh sách ở đoạn Facebook không thoả "cần danh sách" của đoạn TikTok. Số "Bước n" là số TRONG đoạn. */
export function phanTichCanhBao(doThi: DoThi, tuyChon: TuyChonTach): CanhBaoDoan[] {
  const t = tachDoan(doThi, tuyChon);
  const ra: CanhBaoDoan[] = [];
  for (const d of t.duong) for (const m of d.muc) {
    if (m.kieu !== "doan") continue;
    const ops = opsCua(tuyChon.banDo, m.nenTang);
    const pt = ops ? phanTichDoan(m.nut, ops, tuyChon.hoSoTen, tuyChon.mucTieu) : null;
    if (!ops || !pt) continue;
    const tatCa = ops.map((x) => ({ tenNgan: x.tenNgan, taoDs: x.thuTu.taoDs }));
    const them = (loai: CanhBaoDoan["loai"], ds: string[]): void => {
      for (const noi of ds) { const j = Number(/^Bước (\d+)/.exec(noi)?.[1] ?? 0); ra.push({ doanId: m.doanId, ...(j > 0 && m.nut[j - 1] ? { nutId: m.nut[j - 1]!.id } : {}), loai, noi }); }
    };
    them("can_danh_sach", loiCanDanhSach(pt.muc, tatCa));
    them("to_hop", loiToHop(pt.muc));
    them("nghi_lien", loiNghiLien(pt.muc));
  }
  return ra;
}
